import dayjs from 'dayjs';
import { ToWords } from 'to-words';
const toWords = new ToWords({ localeCode: 'en-US' });
import { formatToCurrency } from '@/lib/helpers';
import moment from 'moment';

export const generateLoanApplication = ({
  loan,
  securityDocsUserData,
  borrowerInfo,
  company
}) => {
  //Format Collaterals
  /**
   * Note:
   * Owner's trn and employment details aren't attached to the cash collateral object
   * therefore they cant get added to the cash secured guarantor schedule item
   */
  const collaterals = loan.loanCollaterals.reduce(
    (result, lc) => {
      if (lc.collateral.collateralType.toLowerCase() === 'cash account') {
        return result.cashCollaterals.push({
          accountNumber: (lc.loan.account || '').toUpperCase(),
          marketValue: lc.collateral.marketValue,
          pledgedAmount: lc.pledgedAmount,
          pledgedAmountInWords: toWords(
            Number(lc.pledgedAmount).toFixed(2),
            'currency'
          ).toUpperCase(),
          owners: lc.collateral.collateralToOwner.map((o) => o.owner),
          institution: lc.collateral.institution
        });
      } else if (
        lc.collateral.collateralType.toLowerCase() === 'motor vehicle'
      ) {
        result.motorVehicleCollaterals.push({
          chassisNumber: lc.collateral.chassisNumber,
          engineNumber: lc.collateral.engineNumber,
          registrationNumber: lc.collateral.registrationNumber,
          makeModel: `${lc.collateral.make || ''} ${
            lc.collateral.model || ''
          }`.toUpperCase(),
          year: lc.collateral.year,
          color: (lc.collateral.color || '').toUpperCase(),
          model: (lc.collateral.model || '').toUpperCase(),
          market_Value: lc.collateral.marketValue,
          pledgedAmount: lc.pledged_amount,
          collateralType: lc.collateral.collateralType,
          nameOfInsurer: lc.collateral.nameOfInsurer,
          insuranceAmount: lc.collateral.insuranceAmount,
          insuranceRenewalDate: lc.collateral.insuranceRenewalDate,
          insuranceType: (lc.collateral.insuranceType || '').toUpperCase(),
          nameOfValuator: lc.collateral.nameOfValuator,
          dateOfValuation: lc.collateral.dateOfValuation,
          valuationExpiryDate: lc.collateral.valuationExpiryDate,
          owners: lc.collateral.collateralToOwner.map((o) => o.owner)
        });
      } else if (lc.collateral.collateralType.toLowerCase() === 'real estate') {
        result.motorVehicleCollaterals.push({
          volume: lc.collateral.volume,
          folio: lc.collateral.folio,
          forcedSaleValue: lc.collateral.forcedSaleValue,
          descriptionOfProperty: lc.collateral.descriptionOfProperty,
          external_amount_pledged: lc.collateral.amountPledgedExternal,
          collateralAddress: {
            street: lc.collateral.street,
            town: lc.collateral.town,
            province: lc.collateral.province,
            country: lc.collateral.country
          },
          marketValue: lc.collateral.marketValue,
          mortgage_holder: el.mortgage_holder,
          pledgedAmount: lc.pledgedAmount,
          collateralType: lc.collateral.collateralType,
          nameOfInsurer: lc.collateral.nameOfInsurer,
          insuranceAmount: lc.collateral.insuranceAmount,
          insuranceRenewalDate: lc.collateral.insuranceRenewalDate,
          insuranceType: (lc.collateral.insuranceType || '').toUpperCase(),
          nameOfValuator: lc.collateral.nameOfValuator,
          dateOfValuation: lc.collateral.dateOfValuation,
          valuationExpiryDate: lc.collateral.valuationExpiryDate,
          owners: lc.collateral.collateralToOwner.map((o) => o.owner)
        });
      }
    },
    {
      cashCollaterals: [],
      motorVehicleCollaterals: [],
      realEstateCollaterals: []
    }
  );

  const borrowerMainEmployer = borrowerInfo.employer.find((v) => v.isPrimary);
  const today = {
    day: new Date().getDate(),
    month: new Intl.DateTimeFormat('en-US', { month: 'long' }).format(
      new Date()
    ),
    year: new Date().getFullYear()
  };

  borrowerInfo.fullName = `${borrowerInfo.firstName} ${borrowerInfo.middleName} ${borrowerInfo.lastName}`;

  loan.loanAmountInWords = toWords
    .convert(loan?.loanAmount || 0, { currency: true, doNotAddOnly: true })
    .toUpperCase();
  loan.loanAmountInCurrency = formatToCurrency(loan.loanAmount);
  const combinedShares = (
    Number.parseInt(loan.loanAmount) *
      (Number.parseInt(
        securityDocsUserData?.offerLetter.shareContribution || 0
      ) /
        100) +
    Number.parseInt(securityDocsUserData?.offerLetter.permanentShares || 0)
  ).toFixed(2);

  const loanApplicationClause = [
    {
      text: 'LOAN APPLICATION',
      bold: true,
      alignment: 'center',
      fontSize: 12
    },
    {
      table: {
        widths: ['*', '*'],
        body: [
          [
            `BRANCH: ${loan.accreditedBranch.branchName}`,
            `ACCOUNT#: ${loan.account.mainAccountNumber}`
          ],
          [
            `LOAN PRODUCT: ${loan.loanProduct}`,
            `TOTAL LOAN: ${loan.loanAmountInCurrency}`
          ]
        ]
      },
      margin: [0, 10, 0, 10],

      layout: {
        hLineWidth: function (i, node) {
          return 0;
        },
        vLineWidth: function (i, node) {
          return 0;
        }
      }
    },
    {
      table: {
        widths: ['*', '*', '*'],
        body: [
          [
            {
              text: "BORROWER'S PERSONAL INFORMATION",
              colSpan: 3,
              alignment: 'center',
              fillColor: '#fff5c4'
            },
            {},
            {}
          ],
          [
            { text: `FIRST NAME: ${borrowerInfo.firstName}` },
            { text: `MIDDLE NAME: ${borrowerInfo.middleName}` },
            { text: `LAST NAME: ${borrowerInfo.lastName}` }
          ],

          [
            `TITLE: ${borrowerInfo.title}`,
            `ALIAS: `,
            `TAX NUMBER: ${borrowerInfo.trn}`
          ],
          [
            `DATE OF BIRTH: ${moment(borrowerInfo.dateOfBirth).format(
              'Do MMMM, YYYY'
            )} `,
            `GENDER: ${borrowerInfo.gender} `,
            `MARITAL STATUS: ${borrowerInfo.maritalStatus}`
          ],
          [
            {
              text: 'CONTACT DETAILS',
              colSpan: 3,
              alignment: 'center',
              fillColor: '#fff5c4'
            },
            {},
            {}
          ],
          [
            {
              text: `CELL: ${
                borrowerInfo.contact.find((v) => v.type.toLowerCase() == 'cell')
                  ?.number || ''
              }`
            },
            {
              text: `HOME: ${
                borrowerInfo.contact.find((v) => v.type.toLowerCase() == 'home')
                  ?.number || ''
              }`
            },
            { text: `EMAIL: ${borrowerInfo?.emailAddress || ''}` }
          ],
          [
            {
              text: 'ADDRESS DETAILS',
              colSpan: 3,
              alignment: 'center',
              fillColor: '#fff5c4'
            },
            {},
            {}
          ],
          [
            {
              text: `STREET / DISTRICT: ${borrowerInfo.livingAddress.street}`,
              colSpan: 3
            },
            {},
            {}
          ],
          [
            { text: `CITY: ${borrowerInfo.livingAddress.city}` },
            { text: `PARISH: ${borrowerInfo.livingAddress.province}` },
            { text: `COUNTRY: ${borrowerInfo.livingAddress.country}` }
          ],
          [
            {
              text: `EMPLOYMENT DETAILS`,
              colSpan: 3,
              alignment: 'center',
              fillColor: '#fff5c4'
            },
            {},
            {}
          ],
          [
            {
              text: `NAME OF EMPLOYER: ${borrowerMainEmployer.nameOfEmployer}`,
              colSpan: 3
            },
            {},
            {}
          ],
          [
            { text: `POSITION: ${borrowerMainEmployer.profession}` },
            { text: `INDUSTRY: ${borrowerMainEmployer.businessIndustry}` },
            { text: `SECTOR: ${borrowerMainEmployer.sector}` }
          ],
          [
            {
              text: `YEAR OF EMPLOYMENT: ${moment(
                borrowerMainEmployer.dateEmployed
              ).format('Do MMMM, YYYY')}`
            },
            { text: `TELEPHONE: ${borrowerMainEmployer.contactNumber}` },
            { text: `EMAIL: ${borrowerMainEmployer.email}` }
          ],
          [
            {
              text: `LOCATION: ${borrowerMainEmployer.street}`,
              colSpan: 3
            },
            {},
            {}
          ],
          [
            { text: `CITY: ${borrowerMainEmployer.city}` },
            { text: `PARISH: ${borrowerMainEmployer.state}` },
            { text: `COUNTRY: ${borrowerMainEmployer.country}` }
          ],
          [
            {
              text: `LOAN INFORMATION`,
              colSpan: 3,
              alignment: 'center',
              fillColor: '#fff5c4'
            },
            {},
            {}
          ],
          [
            { text: `LOAN PRODUCT: ${loan.loanProduct}` },
            { text: `LOAN TYPE: ${loan.loanType}` },
            { text: `LOAN ID: ${loan.loanId}` }
          ],
          [
            { text: `METHOD OF PAYMENT: ${loan.paymentMethod}` },
            { text: `TOTAL LOAN: ${loan.loanAmount}` },
            { text: `PAYMENT: ${loan.frequencyPayment}` }
          ],
          [
            {
              text: `LOAN PURPOSE: ${loan.loanProduct}`,
              colSpan: 3
            },
            {},
            {}
          ],
          [
            { text: `TENURE: ${loan.tenure}` },
            { text: `ANNUAL INTEREST: ${loan.interest}` },
            { text: `CREDIT SCORE: ${loan.currentCreditScore}` }
          ]
        ]
      }
    },
    ...collaterals.motorVehicleCollaterals.map((c) => {
      return {
        table: {
          body: [
            [
              {
                text: `COLLATERAL AND INSURANCE INFORMATION`,
                colSpan: 3,
                alignment: 'center',
                fillColor: '#fff5c4'
              },
              {},
              {}
            ],
            [
              {
                text: 'MOTOR VEHICLE COLLATERAL',
                colSpan: 3
              },
              {},
              {}
            ],
            [
              { text: `ENGINE: ${c.engineNumber}` },
              { text: `CHASSIS: ${c.chassisNumber}` },
              { text: `REGISTRATION: ${c.registrationNumber}` }
            ],
            [
              { text: `YEAR: ${c.year}` },
              { text: `COLOR: ${c.color}` },
              { text: `MAKE/MODEL: ${c.makeModel}` }
            ],
            [
              { text: `INSURANCE AMOUNT: ${c.insuranceAmount}` },
              { text: `INSURANCE TYPE: ${c.insuranceType}` },
              { text: `RENEWAL DATE: ${c.insuranceRenewalDate}` }
            ],
            [
              { text: `NAME OF VALUATOR: ${c.nameOfValuator}` },
              { text: `VALUATION DATE: ${c.dateOfValuation}` },
              { text: `VALUATION EXPIRY DATE: ${c.valuationExpiryDate}` }
            ]
          ]
        }
      };
    }),
    {
      text: 'DECLARATION OF BORROWER',
      bold: true,
      margin: [0, 20, 0, 10]
    },
    {
      text: [
        'I, ',
        {
          text: `${borrowerInfo.fullName}`,
          bold: true
        },
        'hereby apply for a loan of ',
        {
          text: `${loan.loanAmountInWords} (${loan.loanAmountInCurrency}) `,
          bold: true
        },
        'and confirm that the information given herein, including all other supported statements, are true in all respects, accurate and complete and that I have not withheld any information that might affect the Credit Union’s decision. I am agreeable to the Credit Union taking such steps as it may deem necessary to verify any of the information given. The Credit Union is authorized to debit my account with the monthly payments on due dates and I agree to pay all appropriate costs incurred for any overdue installments and all fees associated with the processing of the loan. The Credit Union is authorized to provide information to Credit Bureaus and other credit grantors as permitted by law.'
      ],
      margin: [0, 0, 0, 15]
    },
    {
      table: {
        widths: ['*', '*', '*'],
        body: [
          [
            {
              text: `${borrowerInfo.fullName} `,
              bold: false
            },
            {},
            {}
          ]
        ]
      },

      layout: {
        hLineWidth: function (i, node) {
          return 0;
        },
        vLineWidth: function (i, node) {
          return 0;
        },
        paddingTop: function (i, node) {
          return 0;
        },
        paddingBottom: function (i, node) {
          return 0;
        }
      }
    },
    {
      table: {
        widths: ['55%', '35%', '10%'],
        body: [
          [
            {
              text: 'Name of Borrower',
              bold: false
            },
            {
              text: 'Signature of Borrower',
              bold: false
            },
            {
              text: 'Date',
              bold: false
            }
          ]
        ]
      },

      layout: {
        hLineWidth: function (i, node) {
          return i == 0 ? 0.5 : 0;
        },
        vLineWidth: function (i, node) {
          return 0;
        },
        paddingTop: function (i, node) {
          return 0;
        },
        paddingBottom: function (i, node) {
          return 0;
        }
      },
      margin: [0, 0, 0, 25]
    },
    {
      table: {
        widths: ['55%', '35%', '10%'],
        body: [
          [
            {
              text: 'Name of Witness',
              bold: false
            },
            {
              text: 'Signature of Witness',
              bold: false
            },
            {
              text: 'Date',
              bold: false
            }
          ]
        ]
      },

      layout: {
        hLineWidth: function (i, node) {
          return i == 0 ? 0.5 : 0;
        },
        vLineWidth: function (i, node) {
          return 0;
        },
        paddingTop: function (i, node) {
          return 0;
        },
        paddingBottom: function (i, node) {
          return 0;
        }
      },
      pageBreak: 'after'
    },
    {
      text: 'APPROVING OFFICER(S) ACTION',
      style: 'header',
      alignment: 'center',
      bold: true
    },
    {
      text: `I the undersigned hereby confirm that loan in the amount of ${loan.loanAmountInWords} ( ${loan.loanAmountInCurrency} ) was duly approved in accordance with the following terms and conditions :`,
      margin: [0, 0, 0, 10]
    },
    {
      table: {
        body: [
          [
            {
              text: `NAME OF BORROWER: ${borrowerInfo.fullName} `,
              bold: false
            },
            {
              text: `LOAN PRODUCT: ${loan.loanProduct}`,
              bold: false
            },
            {
              text: `PAYMENT METHOD: ${loan.paymentMethod}`,
              bold: false
            }
          ],
          [
            {
              text: 'ANNUAL INTEREST RATE: 28.00%',
              bold: false
            },
            {
              text: `TENURE: ${loan.tenure}`,
              bold: false
            },
            {
              text: `PAYMENT: ${loan.frequencyPayment}`,
              bold: false
            }
          ]
        ]
      }
    },
    {
      text: 'CONDITIONS OF APPROVAL',
      bold: true,
      margin: [0, 20, 0, 0]
    },
    {
      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
      decoration: 'underline',
      margin: [0, 10, 0, 10]
    },
    {
      text: 'OTHER COMMENTS',
      bold: true
    },
    {
      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
      decoration: 'underline',
      margin: [0, 10, 0, 10]
    },
    {
      text: 'APPROVING OFFICER(S)',
      bold: true
    },
    {
      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
      decoration: 'underline',
      margin: [0, 10, 0, 10]
    },
    {
      table: {
        widths: ['*', '*'],
        body: [
          [
            {
              text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
              decoration: 'underline'
            },
            {
              text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
              decoration: 'underline'
            }
          ],
          [
            {
              columns: ['NAME', 'SIGNATURE']
            },
            {
              columns: ['APPROVER', 'SIGNATURE']
            }
          ]
        ]
      },
      layout: {
        hLineWidth: function (i, node) {
          return 0;
        },
        vLineWidth: function (i, node) {
          return 0;
        },
        paddingTop: function (i, node) {
          return 0;
        },
        paddingBottom: function (i, node) {
          return 0;
        }
      }
    },
    {
      table: {
        widths: ['*', '*', '*'],
        body: [
          [
            {
              text: '',
              bold: false,
              fillColor: '#fff5c4'
            },
            {
              text: 'OTHER SIGNATORIES',
              bold: false,
              alignment: 'center',
              fillColor: '#fff5c4'
            },
            {
              text: '',
              bold: false,
              fillColor: '#fff5c4'
            }
          ],
          [
            {
              text: 'NAME',
              bold: false
            },
            {
              text: 'POSITION',
              bold: false
            },
            {
              text: 'SIGNATURE',
              bold: false
            }
          ],
          ...Array.from({ length: 5 }, () => [
            {
              text: 'ㅤ',
              bold: false
            },
            {
              text: 'ㅤ',
              bold: false
            },
            {
              text: 'ㅤ',
              bold: false
            }
          ])
        ]
      },
      layout: {
        hLineWidth: function (i, node) {
          return 0.5;
        },
        vLineWidth: function (i, node) {
          return 0.5;
        },
        paddingTop: function (i, node) {
          return 5;
        },
        paddingBottom: function (i, node) {
          return 0;
        }
      },
      pageBreak: 'after',
      margin: [0, 20, 0, 20]
    }
  ];

  const cashSecuredBlock = [
    {
      text: 'LOAN AGREEMENT',
      alignment: 'center',
      bold: true
    },
    {
      text: `THIS INSTRUMENT is made on the ${today.day} day of ${today.month} ${today.year} BETWEEN the party set out at Item 1 of the Schedule hereto (hereinafter referred to as “the BORROWER” ) which expression where the context so admits  shall include his/her Heirs, successors, executors assigns and Personal Representatives, and COK SODALITY CO-OPERATIVE CREDIT UNION LIMITED , a Co-operative Society duly incorporated under the Co-operative Societies Act of Jamaica with Registered Office at 66 Slipe Road, Kingston 5 in the parish of Saint Andrew (hereinafter referred to as “COK SODALITY”) which expression shall include its successors and assigns of the THIRD PART.`,
      margin: [0, 0, 0, 20]
    },
    {
      table: {
        body: [
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'This document shall be referred to as the LOAN AGREEMENT.',
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: [
                'The BORROWER has requested from COK SODALITY a loan facility',
                {
                  text: ` ${loan.loanAmountInWords} (${loan.loanAmountInCurrency}) `,
                  bold: true
                },
                '(hereinafter referred to as the “LOAN SUM”)'
              ],
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'The LOAN SUM is to be repaid along with interest at the rate of interest set out at Item 3 of the Schedule hereto (hereinafter referred to as ”the RATE OF INTEREST”).',
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: `COK SODALITY has agreed to Lend subject to the terms and conditions hereinafter contained and the terms and conditions of an Offer Letter by COK SODALITY dated the ${moment(
                securityDocsUserData?.offerLetter
              ).format(
                'Do [Day of] MMMM, YYYY'
              )} hereinafter referred to as “the Offer Letter”).`,
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: `The purpose of the loan is ${loan.loanPurpose}`,
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'The Offer Letter shall be read together with this Agreement in any event for litigation purposes or otherwise.',
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'COK SODALITY is relying on information in deciding to grant the loan request. The BORROWER represent, warrant and certify that the information provided in all documents submitted as security for the loan as true, correct and complete.',
              bold: false
            }
          ]
        ]
      },
      layout: {
        hLineWidth: function (i, node) {
          return 0;
        },
        vLineWidth: function (i, node) {
          return 0;
        },
        paddingTop: function (i, node) {
          return 10;
        },
        paddingBottom: function (i, node) {
          return 10;
        }
      }
    },
    {
      ol: generateNumberedList([
        [
          'AVAILABILITY CONDITIONS',
          {
            ol: [
              [
                'LOAN SUM will be made available for disbursement to the BORROWER :',
                {
                  ol: [
                    [
                      'On compliance with the terms and conditions of the Offer Letter; and'
                    ],
                    [
                      'upon completion of all other related documents; and any security documentation as COK SODALITY and its Attorney(s) in their sole discretion may deem necessary for the transaction.'
                    ],
                    [
                      'payment of the relevant commitment fees, legal fees and stamp duties associated with the preparation, stamping and registration of this and any other security documentation together with General Consumption Tax (GCT) as applicable'
                    ]
                  ]
                }
              ],
              [
                'The representations and warranties set forth herein shall be true on and as of the date of disbursement with the same effect as though such representations and warranties had been made on and as of the date of such disbursement.'
              ],
              [
                'The securities shall have been entered into between the respective parties thereto and shall each become unconditional and fully effective in accordance with their terms.'
              ],
              [
                'The BORROWER shall disclose to COK SODALITY:',
                {
                  ol: [
                    [
                      'anything which has happened which may:',
                      {
                        ol: [
                          [
                            'materially or adversely affect:',
                            {
                              ol: [
                                ['the carrying out of the undertaking; or'],
                                ['he business prospects; or'],
                                ['financial conditions of the BORROWER ; or']
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      'make it difficult for the BORROWER be able to fulfil obligations:'
                    ],
                    ['to COK SODALITY hereunder; or'],
                    ['under any securities.']
                  ]
                }
              ]
            ]
          }
        ],
        [
          'SECURITY',
          {
            ol: [
              [
                'As security for the Repayment of the loan; payment of interest; and all other amounts owing by the BORROWER to COK SODALITY under these presents or under any of the securities, the BORROWER has issued; shall issue; or cause to be issued, to COK SODALITY the following:-',
                {
                  ol: [
                    [
                      `Shares in the sum of ${combinedShares} (Ordinary and Permanent);`
                    ],
                    ['Set-off Agreement;'],
                    [
                      `Promissory Note from the borrower in the amount of ${loan.loanAmountInCurrency}`
                    ],
                    [
                      'Standard Cuna Mutual Loan Protection Insurance covering of total loan amount held with COK SODALITY.'
                    ],
                    [
                      `Hypothecation of ${collaterals.cashCollaterals.map(
                        (col) =>
                          `${col.pledgedAmountInWords} held at ${
                            col.institution
                          } A/C #${
                            col.accountNumber
                          }  in the name of ${col.owners
                            .map((o) => o.ownerName)
                            .join(',')}`
                      )}`
                    ]
                  ]
                }
              ],
              [
                'The aforementioned securities in Clause 2 shall be valid, binding and enforceable against the BORROWER and/or other security party.'
              ]
            ]
          }
        ],
        [
          'REPRESENTATION AND WARRANTIES',
          {
            ol: [
              [
                'The BORROWER represents and warrants to and undertakes with COK SDALITY that:-',
                {
                  ol: [
                    [
                      'THE POWERS OF THE BORROWER',
                      {
                        ol: [
                          [
                            'BORROWER has full power to:',
                            {
                              ol: [
                                [
                                  'enter into the obligations contained herein; and'
                                ],
                                ['incur the loan contemplated herein; and'],
                                [
                                  'execute deliver and perform this Agreement and any Security documents required, on the terms and conditions herein and therein contained.'
                                ]
                              ]
                            }
                          ],
                          [
                            'The BORROWER has taken all necessary action to authorize execution and delivery of this Agreement hereunder and no limit on the powers of the BORROWER to borrow will be exceeded as a result of such borrowing.'
                          ],
                          [
                            'Neither the execution nor delivery of this Agreement and the Security Documentation nor the transaction herein or therein contemplated nor the compliance with the terms and conditions hereof will:'
                          ],
                          [
                            'contravene any:',
                            {
                              ol: [
                                ['provision of Laws statute decree rule;'],
                                [
                                  'regulation to which the BORROWER is subject; 3.1.1.4.3. judgment decree franchise order; or 3.1.1.4.4. permit applicable to it. '
                                ]
                              ]
                            }
                          ],
                          [
                            'conflict with or result in any breach: of any of the terms; covenants; conditions; provisions; constitute a default under; result in the creation; or importation of any lien, security, interest, charge or encumbrance upon any of the property or assets of the BORROWER pursuant to the terms of any deed agreement mortgage deed of trust or other instrument to which it is: a party; is subject; or by which it may be bound, save as disclosed in writing to COK SODALITY prior to the date of this Agreement.'
                          ],
                          [
                            {
                              text: 'NO CHARGE',
                              decoration: 'underline'
                            },
                            {
                              ol: [
                                [
                                  'No charge in favour of any person exists:',
                                  {
                                    ol: [
                                      [
                                        'over the whole or any part of the assets; or'
                                      ],
                                      [
                                        'undertaking other than any charges in connection with these presents currently exists; and 3.1.2.1.3. will not  so exist after the date of this Agreement.'
                                      ],
                                      [
                                        'The BORROWER will not without the prior written approval of COK SODALITY grant or establish or permit  to arise or subsist any charge in favour of any other person.'
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ],
                          [
                            {
                              text: 'NO DEFAULT IN ANY OTHER PAYMENTS',
                              decoration: 'underline'
                            },
                            {
                              ol: [
                                [
                                  'The BORROWER is not in default under any Agreement to which the BORROWER is a party or by which  the BORROWER may be bound in respect to:'
                                ],
                                ['the payment; or'],
                                [
                                  'performance of any obligations for borrowed money.'
                                ],
                                [
                                  'No event has occurred or is continuing with respect to the BORROWER that would adversely affect the BORROWER ’s ability to repay the loan hereby created.'
                                ]
                              ]
                            }
                          ],
                          [
                            {
                              text: 'NO OTHER PROCEEDINGS AGAINST BORROWER',
                              decoration: 'underline'
                            },
                            {
                              ol: [
                                [
                                  'No litigation or administrative proceeding is presently:',
                                  {
                                    ol: [
                                      ['in course; or'],
                                      [
                                        'to the knowledge of the BORROWER pending; or'
                                      ],
                                      [
                                        'threatened or',
                                        {
                                          text: '-which would have a material or adverse effect on the financial condition of the BORROWER except as disclosed to COK SODALITY in writing prior to the date hereof.',
                                          margin: [0, 10, 0, 0]
                                        }
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ],
                          [
                            {
                              text: 'ACCURACY OF FINANCIAL STATEMENTS AND DOCUMENTS',
                              decoration: 'underline'
                            },
                            {
                              ol: [
                                [
                                  'The financial statements and other documents supplied to COK SODALITY by or on behalf of the BORROWER are:',
                                  {
                                    ol: [
                                      [
                                        'true and correct in all material respects; and'
                                      ],
                                      [
                                        'fairly represents the financial condition of the BORROWER; and'
                                      ],
                                      [
                                        "the result of bona fide projections of the BORROWER's operations for the period covered; and"
                                      ],
                                      [
                                        'financial statements prepared in accordance with the accounting principles generally accepted and applied on a consistent basis.'
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ],
                          [
                            {
                              text: 'NO PREJUDICE TO RIGHTS & REMEDIES AVAILABLE TO COK SODALITY',
                              decoration: 'underline'
                            },
                            {
                              ol: [
                                [
                                  'any investigation by or on behalf of COK SODALITY into the affairs of the BORROWER ; or',
                                  {
                                    ol: [
                                      [
                                        'The rights and remedies of COK SODALITY in relation to any misrepresentations or breach of warranty on the part of the BORROWER shall not be prejudiced by:'
                                      ],
                                      [
                                        'he execution or performance of this Agreement; or'
                                      ],
                                      [
                                        'any other act or thing, which may be done by or on behalf of COK SODALITY in connection with this Agreement and which might apart from this provision prejudice such rights or remedies.'
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ],
                          [
                            {
                              text: 'APPROVALS OF AUTHORITES AND AGENCIES',
                              decoration: 'underline'
                            },
                            {
                              ol: [
                                [
                                  'Any necessary approval or authorization from any government department authority or agency required in connection with the execution, delivery, performance, validity, or enforceability of this Agreement and the security documentations:'
                                ],
                                ['has been obtained; and'],
                                [
                                  'will be kept current, in full force and effect.'
                                ],
                                [
                                  'This Agreement constitutes and the security documentation, when executed, will constitute the legal, valid and binding obligations of the BORROWER enforceable in accordance with their respective terms.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ]
                  ]
                }
              ]
            ]
          }
        ],
        [
          {
            text: 'COVENANTS',
            decoration: 'underline'
          },
          {
            ol: [
              [
                'The BORROWER covenants and agrees that:-',
                {
                  ol: [
                    [
                      {
                        text: 'USE OF LOAN SUM',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'the LOAN SUM shall be used for the purpose set out in the Offer Letter; and'
                          ],
                          [
                            'the LOAN SUM shall not be used for any purpose except the Purpose in the Offer Letter; and'
                          ],
                          [
                            'failure to comply with clauses 4.1.1.1 and 4.1.1.2 shall not prejudice any rights of COK SODALITY; and'
                          ],
                          [
                            'COK SODALITY shall not be responsible for monitoring or ensuring the use or application by the BORROWER of the LOAN SUM.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'REPAYMENT OF LOAN',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'LOAN SUM and interest shall be payable:',
                            {
                              ol: [
                                ['in monthly instalments; and'],
                                [
                                  'in full, without any set-off or counterclaim; and'
                                ],
                                [
                                  'commencing one calendar month after the date of disbursement of the LOAN SUM.'
                                ],
                                [
                                  'One-month instalment of the LOAN SUM and interest will be taken from loan proceeds and paid upfront on LOAN SUM upon disbursement; and'
                                ],
                                [
                                  'Punctual payment shall be made to COK SODALITY of each monthly instalment due and payable until the LOAN SUM is fully repaid; and'
                                ],
                                [
                                  'COK SODALITY reserves the right to withdraw any shortfall from any savings subaccount maintained by the BORROWER to offset arrears; in the event the BORROWER makes monthly payments, which fall short of the prescribed monthly amount. COK SODALITY may appropriate such payment towards such of the obligations of the BORROWER as COK SODALITY may decide.The BORROWER waives any right to make an appropriation in respect of a partial payment. Any appropriation by COK SODALITY shall apply to the exclusion of any actual or purported appropriation by the BORROWER.'
                                ],
                                [
                                  'payment shall be made to COK SODALITY, on demand of all sums of money:',
                                  {
                                    ol: [
                                      [
                                        'which now or at any time hereafter may be due or owing by the BORROWER to COK SODALITY on any current or other account or in any manner whatever; and'
                                      ],
                                      [
                                        'to discharge all liabilities incurred by the BORROWER to COK SODALITY in any manner whatever (and in the case of both money owing and liabilities incurred whether alone or jointly with any other person and in whatever style or name and whether such moneys are owed or liabilities are incurred as principal or surety for the payment of the indebtedness to COK SODALITY of any person, limited company or other corporation, firm, committee, association or other unincorporated body whatsoever).'
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'INTEREST',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'All outstanding sums including any commission, discount and other banking charges, legal and other costs, charges and expenses shall be repaid to COK SODALITY with interest to the date of payment.'
                          ],
                          [
                            'Interest shall be:',
                            {
                              ol: [
                                [
                                  'computed at the rate set out in Item 3 of the said Schedule; or at such other rate or rates of interest as COK SODALITY shall from time to time charge; and'
                                ],
                                [
                                  'computed daily at such rate or rates as aforesaid in clause 4.1.3.2.1 (as well after as before any judgment for the recovery of the moneys due or owing or for which the BORROWER may become liable to COK SODALITY as aforesaid or any part thereof); and'
                                ],
                                [
                                  'payable on such day in every month as COK SODALITY may from time to time determine.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'LATE PENALTY',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'A late penalty of 2% per month on the total arrears of monthly instalments shall be payable on any sum not  punctually paid on the due date for two or more months (consecutive or combined).'
                          ],
                          [
                            'Late penalties shall accrue from the second month and each month thereafter until all arrears have been paid but  without prejudice to the any remedies available to COK SODALITY hereunder or under any securities or otherwise'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'FEES AND EXPENSES',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'On demand, to pay any fees of and incidental to the preparation, stamping and completion of this security and the protection realization and enforcement thereof and otherwise in relation thereto.'
                          ],
                          [
                            'The BORROWER shall also pay such commitment or processing fees as may be levied by COK SODALITY from time to time.'
                          ],
                          [
                            'Payment shall be made, on demand and on a full indemnity basis, to COK SODALITY the amount of all costs and expenses (including but not limited to legal fees, professional fees, statutory costs [including stamp duty and registration fees] and out-of-pocket expenses) which COK SODALITY incurs in connection with the preparation, negotiation, execution and delivery of this Agreement and any Related Document.'
                          ],
                          [
                            'Payment shall be made, on demand and on a full indemnity basis, to COK SODALITY all costs and expenses (including but not limited to legal fees, professional fees, statutory costs [including stamp duty and registration fees] and out-of-pocket expenses) incurred by COK SODALITY in connection with any actual or proposed amendment or extension of or any waiver or consent under this Agreement and in contemplation of or otherwise in connection with the enforcement (or attempted enforcement) of, or preservation (or attempted preservation) of any rights under, this Agreement and/or any related document or otherwise in respect of any moneys from time to time owing under this Agreement.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: '4.1.6	RIGHT TO PUBLISH',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            "So long as there is a default in payment of any sums owed by the BORROWER, COK SODALITY further reserves the right to publish the BORROWER's name, address, occupation and any other details which COK SODALITY may see fit in any periodical newspaper or media in any manner COK SODALITY sees fit."
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'CONCLUSIVE EVIDENCE',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            [
                              'Any statement of an officer of COK SODALITY as to the amount remaining unpaid to COK SODALITY at  any time by the Borrower shall be conclusive evidence and shall, in any event, be prima facie evidence as to  the amount remaining unpaid to COK SODALITY at such time by the Borrower'
                            ]
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: "COK SODALITY's RIGHT TO VARY TIME AND INTEREST",
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'COK SODALITY shall be at liberty from time to time to give time:',
                            {
                              ol: [
                                [
                                  'for payment of any bill; bills of exchange; promissory note; or other security; or securities which may have been discounted for or received on account from the BORROWER by COK SODALITY or on which the BORROWER shall or may be liable as drawer or endorser or otherwise to any party or parties thereto or liable thereon as COK SODALITY shall in its discretion think fit.'
                                ],
                                [
                                  "That without prejudice to any of the COK SODALITY's other powers, COK SODALITY shall have power  from time to time in respect of moneys due to vary whether; and"
                                ],
                                [
                                  'by way of increase or reduction of the RATE OF INTEREST then payable hereunder; and'
                                ],
                                [
                                  'the dates on and the periods for which unpaid interest shall attract a penalty; an'
                                ],
                                [
                                  'COK SODALITY shall not be required to give any prior notice whatsoever of any such variation to the  BORROWER.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'DUTY TO PAY TAXES AND OTHER CHARGES',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'The BORROWER will duly and punctually pay or cause to be paid all taxes, assessments, duties and other charges imposed or charged upon any properties and assets which the BORROWER is liable by Law to pay except that no such tax assessments duty or charge need to be paid if being contested in good faith and by appropriate proceedings promptly initiated and urgently conducted and if such reserve or other appropriate provisions as required by generally accepted accounting principles shall have been made thereafter.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: '4.1.10	ADVANCE PAYMENTS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'It shall be lawful for but not obligatory on the COK SODALITY:'
                          ],
                          [
                            'to advance and pay all moneys necessary for remedying any breach or non-observance by the BORRWER of any of the BORRWER’s covenant’s or stipulations hereunder and all costs, charges and expenses properly incurred hereunder by COK SODALITY and all other moneys properly in time or respective times of paying or advancing the same; and'
                          ],
                          [
                            'any sum so advanced or paid shall be treated as an addition to the LOAN SUM and the BORROWER hereby irrevocably authorizes COK SODALITY to do so; and'
                          ],
                          [
                            'any such sum shall be repayable on demand and in the meantime shall be hereby secured; and'
                          ],
                          [
                            'this security shall be in addition and without prejudice to any and every other remedy, lien or security which  COK SODALITY may or but for the said charge would have for the moneys hereby secured or any part thereof.'
                          ],
                          [
                            'The total amount of the money secured or to be ultimately recoverable hereunder begin uncertain and without any limit this indenture shall be impressed in the first instance with stamp duty, where applicable, to cover the amount of money specified in Item 4 of the Schedule.'
                          ],
                          [
                            'Notwithstanding Clause 4.1.10.2 COK SODALITY shall be and is hereby empowered at any time or times hereafter (without further licence or consent of the BORROWER) to impress additional stamp duty hereon covering any sum or sums by which the said indebtedness may exceed the amount of money specified in Item 4 of the Schedule hereto it being the intent of these presents that until its discharge the Mortgage, Bill of Sale or Set Off, whichever is applicable, hereby created shall be a continuing security covering the indebtedness of the BORROWER to COK SODALITY for such amount of money as the stamp duty impressed hereon will extend to cover.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NO OBLIGATION TO LEND',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'COK SODALITY shall not be under any obligation to afford or continue credit or facilities to the  BORROWER to any aggregate sum in excess of such limit and extent as COK SODALITY may, in its  absolute discretion, from time to time think fit.'
                          ],
                          [
                            "COK SODALITY may at any time or times require the reduction or discharge of the BORROWER's indebtedness and the BORROWER shall be bound to comply immediately with every such requisition or demand."
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'AUDITED AND UNAUDITED FINACIAL STATEMENTS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'If the BORROWER is a corporate body or partnership or self-employed, then so long as the LOAN SUM or any part thereof or interest thereon or other sums payable thereunder remain outstanding the BORROWER will provide COK SODALITY with:'
                          ],
                          [
                            'unaudited financial statements in respect of its business within sixty (60) days of the end of each quarter; and'
                          ],
                          [
                            'audited financial statements within one hundred and twenty (120) days of the end of their respective financial year.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NO WAIVER OF RIGHTS OR REMEDIES',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'No delay, neglect, omission or forbearance on the part of COK SODALITY to take advantage of or enforce any right or remedy arising out of any breach or non-observance of any covenant or condition herein contained or implied shall be deemed to be or operate as a general waiver of such covenant or condition or the right to enforce or take advantage of the same in respect of any breach or non-observance thereof either original or recurring.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'INDEMNITY',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'COK SODALITY shall not be liable for any involuntary loss which may occur by reason of the exercise or execution (whether contemporaneously or otherwise) of any or all of its rights, remedies and powers conferred, given or implied by this indenture or by Law.'
                          ],
                          [
                            'The BORROWER hereby indemnifies COK SODALITY against any liability, loss or expense incurred by  COK SODALITY as a consequence of:',
                            {
                              ol: [
                                [
                                  'any default in payment by the BORROWER of any sum under this Agreement when due; or'
                                ],
                                ['the occurrence of any Event of Default; or'],
                                [
                                  'any repayment or prepayment of the Facility or part thereof being received otherwise than as set out in this Agreement'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NO CHANGE IN STATUS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'COK SODALITY shall not be liable for any involuntary loss which may occur by reason of the exercise or execution (whether contemporaneously or otherwise) of any or all of its rights, remedies and powers conferred, given or implied by this indenture or by Law.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'CHANGE IN STATUS OR BUSINESS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'There will be no change in the nature of the business or status of the BORROWER or assets financed by the loan by reason of any sale, merger or consolidation without the written notice to COK SODALITY.'
                          ],
                          [
                            'That upon written notice to COK SODALITY of any change in the business or status of the BORROWER or  assets financed COK SODALITY shall have the right, at its absolute discretion, to choose to discontinue its obligations and require immediate payment of any sums owed.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'SECURITIES',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'That this security shall not be affected by nor affect any other security which COK SODALITY may now or hereafter hold from the BORROWER; and COK SODALITY shall be at liberty to realize the securities in such order and manner and to apply and appropriate:',
                            {
                              ol: [
                                [
                                  'any moneys at any time or times paid by or on behalf of the BORROWER ; or resulting from a realization of this or any other security; or'
                                ],
                                [
                                  'any part thereof to such account or item of indebtedness - in such sequence, priority and order as COK SODALITY may, in its absolute discretion, from time to time determine, any direction from the BORROWER to the contrary notwithstanding.'
                                ],
                                [
                                  'This security shall not be considered as satisfied or discharged by any intermediate payment of the whole or part of the moneys owing as aforesaid but shall constitute and be a continuing security to COK SODALITY notwithstanding:',
                                  {
                                    ol: [
                                      [
                                        'any settlement of account; or other matter or thing whatsoever; and'
                                      ],
                                      [
                                        'shall be in addition to any other security or securities which COK SODALITY may from time to time hold or take from the BORROWER and shall not be affected or prejudiced by or affect or prejudice in any way any other security or securities which COK SODALITY may now or hereafter hold or take from the BORROWER.'
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'DEFAULT EVENT',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            "The COK SODALITY shall be promptly notified of the occurrence of any event or circumstances which after notice or lapse of time or both, would constitute an Event of Default and of any matter which might materially or adversely affect the financial condition of the BORROWER or the BORROWER's assets."
                          ],
                          [
                            'COK SODALITY may (without prejudice to any of its other rights) upon and at any time after the happening of an Event of Default, by notice to the BORROWER declare that all sums owing by the BORROWER has become immediately due and payable, whereupon the BORROWER shall forthwith repay the same together with all due and all other sums payable under this Agreement.'
                          ],
                          [
                            'There shall be an Event of Default if:',
                            {
                              ol: [
                                [
                                  'the BORROWER fails to pay any sum payable by the BORROWER under this Agreement and/or any  Securities when due, in the currency and manner provided in this Agreement; or'
                                ],
                                [
                                  'the BORROWER commits any breach of any provision of this Agreement and/or any Securities which  COK SODALITY, in its, sole discretion, deems to be a materially adverse breach; or'
                                ],
                                [
                                  'the BORROWER commits any breach of any other provision of this Agreement and/or any Securities and such breach is, in the reasonable opinion of COK SODALITY, not capable of remedy or such breach is in the reasonable opinion of COK SODALITY capable of remedy and is not remedied within fourteen (14) days after the earlier of the date of notice by COK SODALITY requiring such remedy or the date on which the BORROWER first becomes aware of the breach; or'
                                ],
                                [
                                  'any misrepresentation: made or deemed to be made or repeated by the BORROWER in or pursuant to these presents and/or its securities; or'
                                ],
                                [
                                  'the BORROWER is adjudicated or found to be, insolvent/Bankrupt or stops or suspends payment of the BORROWER’s debts or is (or is deemed to be) unable to or admits inability to pay debts as they fall due or proposes or enters into any voluntary arrangement or any composition or other arrangement for the benefit of the BORROWER’s creditors generally or proceedings are commenced in relation to the BORROWER under any law, regulation or procedure relating to reconstruction or adjustment of debts; or'
                                ],
                                [
                                  'any other event or series of events or any circumstances whether related or not (including but without limitation any adverse change in the employment status, assets or financial condition of the BORROWER which, in the opinion of COK SODALITY, may have a material adverse effect on the BORROWER or the BORROWER’s ability or willingness to perform or comply with any of its obligations under this Agreement and/or its securities.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ]
                  ]
                }
              ],
              [
                {
                  text: 'NOTICE',
                  decoration: 'underline'
                },
                {
                  ol: [
                    [
                      'Any demand for payment or any other demand or notice hereunder:',
                      {
                        ol: [
                          [
                            'may be properly and effectually made by any Manager or Assistant Manager of any branch of COK SODALITY or by an agent, attorney or Attorney-at-Law of COK SODALITY;'
                          ],
                          [
                            'by letter sent by registered post, addressed to the BORROWER at the address given in this security or at the last known place of business of the BORROWER; andSODALITY or by an agent, attorney or Attorney-atLaw of COK SODALITY;'
                          ],
                          [
                            'shall be deemed to have been made on the third day following the day the letter was posted in any post office in  Jamaica.'
                          ]
                        ]
                      }
                    ]
                  ]
                }
              ],
              [
                {
                  text: 'SATISFACTION OF THE LOAN',
                  decoration: 'underline'
                },
                {
                  ol: [
                    [
                      'COK SODALITY may cause satisfaction to be entered on the margin of the record of these presents:',
                      {
                        ol: [
                          ['at the request of the BORROWER; and'],
                          ["at the COK SODALITY's sole discretion; and"],
                          [
                            'upon payment by the BORROWER to COK SODALITY of any costs which it may have otherwise incurred in the protection, enforcement or realization of this security.'
                          ],
                          [
                            {
                              text: 'DUTY TO NOTIFY',
                              decoration: 'underline'
                            },
                            {
                              ol: [
                                [
                                  'As from the date hereof and so long as the loan or any part thereof or any interest thereon remains outstanding both the BORROWER has a duty to inform COK SODALITY promptly, in writing, of :',
                                  {
                                    ol: [
                                      [
                                        'the happening of any Default Event or any event likely to have a substantial adverse effect on their: financial position or operations.'
                                      ],
                                      [
                                        'Any unusual or long-term contract likely by reason of its nature or magnitude, which may affect the interest of COK SODALITY.'
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ],
                          [
                            {
                              text: 'AUTHORITY TO COMPLETE DOCUMENTS',
                              decoration: 'underline'
                            },
                            {
                              ol: [
                                [
                                  'COK SODALITY hereby irrevocably authorises COK SODALITY to supply any information on any document and/or complete any document signed by the BORROWER in blank or incomplete, including the insertion of dates, amounts, names or descriptions so as to render such document compete and enforceable with respect to the Facility or any collateral provided by me to secure the same.'
                                ]
                              ]
                            }
                          ],
                          [
                            {
                              text: 'DATA PROTECTION',
                              decoration: 'underline'
                            },
                            {
                              ol: [
                                [
                                  'As far as is reasonably required, for the service being offered by COK SODALITY in regards to the execution and performance of this Agreement and its securities: the BORROWER hereby consent to the collection, use, disclosure and processing of personal data and information provided by the BORROWER to COK SODALITY or otherwise obtained.'
                                ],
                                [
                                  'Personal data and information reasonably required, in accordance with CLAUSE 4.3.4.1 may be used by, disclosed to or shared with the following:',
                                  {
                                    ol: [
                                      [
                                        'Senior Management and regular employees of COK SODALITY’s various departments and branches for maintaining of accurate records and financial statements;'
                                      ],
                                      [
                                        'Local Authorities under statutory legislations such as the Credit Reporting Act;'
                                      ],
                                      [
                                        'debt collectors; the Court and legal advisors pursuant to enforcement provisions;'
                                      ],
                                      [
                                        'Any other individual in the context of execution and performance of this Agreement and its securities.'
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ],
                          [
                            {
                              text: 'CREDIT REPORTING',
                              decoration: 'underline'
                            },
                            {
                              ol: [
                                [
                                  'The BORROWER hereby consents:',
                                  {
                                    ol: [
                                      [
                                        'To Credit Bureaus licensed under the Credit Reporting Act disclosing credit information about the  BORROWER to COK SODALITY;'
                                      ],
                                      [
                                        'To COK SODALITY disclosing credit information about the BORROWER to Credit Bureaus licensed under the Credit Reporting Act; and'
                                      ],
                                      [
                                        'COK SODALITY providing a signed copy of this agreement or any securities or otherwise to any Credit Bureaus licensed under the Credit Reporting Act by electronic or any other means.'
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ]
                  ]
                }
              ]
            ]
          }
        ]
      ]),
      type: 'none'
    },
    {
      text: 'Ac# 6446663',
      bold: true,
      alignment: 'center'
    },
    {
      text: 'SCHEDULE',
      decoration: 'underline',
      alignment: 'center'
    },
    {
      table: {
        widths: ['auto', '*', '*'],
        body: [
          [
            'ITEM 1',
            'THE BORROWER',
            {
              text: [`${borrowerInfo.fullName}`, '\n', 'TRN: 100000967']
            }
          ],
          ['ITEM 2', 'THE GUARANTOR', ''],
          [
            'ITEM 3',
            'THE RATE OF INTEREST',
            `${loan.interest}% per annum on the reducing balance`
          ],
          [
            'ITEM 4',
            'AMOUNT BORROWED',
            `${loan.loanAmountInWords} (${loan.loanAmountInCurrency})`
          ]
        ]
      }
    },
    {
      pageBreak: 'before',
      table: {
        widths: ['70%', '30%'],
        body: [
          [
            {
              text: [
                'SIGNED by',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'AUTHORIZED SIGNATORY (1)'
              ]
            },
            {}
          ],
          [
            {
              text: [
                'Title:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'Branch:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                }
              ]
            },
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                      decoration: 'underline',
                      alignment: 'center'
                    }
                  ],
                  [
                    {
                      text: 'SIGNATURE OF AUTHORIZED SIGNATORY (1)',
                      alignment: 'center'
                    }
                  ]
                ]
              },
              layout: {
                // default border settings
                hLineWidth: function (i, node) {
                  return 0;
                },
                vLineWidth: function (i, node) {
                  return 0;
                }
              }
            }
          ],
          [
            {
              text: [
                'SIGNED by',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'AUTHORIZED SIGNATORY (2)'
              ]
            },
            {}
          ],
          [
            {
              text: [
                'Title:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'Branch:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                }
              ]
            },
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                      decoration: 'underline',
                      alignment: 'center'
                    }
                  ],
                  [
                    {
                      text: 'SIGNATURE OF AUTHORIZED SIGNATORY (2)',
                      alignment: 'center'
                    }
                  ]
                ]
              },
              layout: {
                // default border settings
                hLineWidth: function (i, node) {
                  return 0;
                },
                vLineWidth: function (i, node) {
                  return 0;
                }
              }
            }
          ],
          [
            {
              text: [
                'before me:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'Witness'
              ]
            },
            {}
          ],
          [
            {
              text: [
                'at',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'in the parish of',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                }
              ]
            },
            {}
          ],
          [
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                      decoration: 'underline'
                    }
                  ],
                  [
                    {
                      text: 'SIGNATURE OF WITNESS:',
                      margin: [0, 0, 0, 10]
                    }
                  ]
                ]
              },
              layout: {
                // default border settings
                hLineWidth: function (i, node) {
                  return 0;
                },
                vLineWidth: function (i, node) {
                  return 0;
                }
              }
            },
            {}
          ]
        ]
      },
      layout: {
        // default border settings
        hLineWidth: function (i, node) {
          return 0;
        },
        vLineWidth: function (i, node) {
          return i === 1 ? 0.1 : 0;
        },
        paddingTop: function (i, node) {
          return 10;
        },
        paddingBottom: function (i, node) {
          return 10;
        }
      }
    },
    {
      margin: [0, 40, 0, 0],
      table: {
        widths: ['70%', '30%'],
        body: [
          [
            {
              text: 'SIGNED by MARCIA BROWN-ELLIOTT, MEMBER'
            },
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                      decoration: 'underline'
                    }
                  ],
                  ['Signature of Member Date :']
                ]
              },
              layout: {
                // default border settings
                hLineWidth: function (i, node) {
                  return 0;
                },
                vLineWidth: function (i, node) {
                  return 0;
                }
              }
            }
          ],
          [
            {
              text: [
                'before me:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'Witness'
              ]
            },
            {}
          ],
          [
            {
              text: [
                'at',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'in the parish of',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                }
              ]
            },
            {}
          ],
          [
            {
              widths: ['*'],
              table: {
                body: [
                  [
                    {
                      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                      decoration: 'underline'
                    }
                  ],
                  ['Signature of Witness	Date']
                ]
              },
              layout: {
                // default border settings
                hLineWidth: function (i, node) {
                  return 0;
                },
                vLineWidth: function (i, node) {
                  return 0;
                }
              }
            },
            {}
          ]
        ]
      },
      layout: {
        // default border settings
        hLineWidth: function (i, node) {
          return 0;
        },
        vLineWidth: function (i, node) {
          return i === 1 ? 0.1 : 0;
        },
        paddingTop: function (i, node) {
          return 10;
        },
        paddingBottom: function (i, node) {
          return 10;
        }
      }
    }
  ];

  const regularBlock = [
    [
      {
        text: 'LOAN AGREEMENT',
        alignment: 'center',
        bold: true,
        margin: [0, 0, 0, 30]
      },
      {
        text: 'For value received, hereinafter called the loan , I/We MARCIA BROWN-ELLIOTT as Principal with Tax Registration Number 100011072 , hereinafter called the Borrower (waiving rights and rights of demand and notice) promise to pay to , hereinafter called the Credit Union, the sum of ONE HUNDRED THOUSAND DOLLARS AND ZERO CENTS ( $100,000.00 ) as follows :',
        margin: [0, 0, 0, 30]
      },
      {
        text: 'The first payment of DOLLARS ( ) shall be paid within days of the date of the first disbursement and DOLLARS ( ) for each succeeding thereafter; the whole sum to be repaid within UNDEFINED UNDEFINED(S) from the date of the first disbursement; interest to be repaid at a rate of 28.00% annually. As collateral security for this loan said Borrower has pledged the following :',
        margin: [0, 0, 0, 30]
      },
      {
        text: 'MOTOR VEHICLE COLLATERAL',
        margin: [0, 0, 0, 10],
        bold: true
      },
      {
        table: {
          body: [
            [
              {
                text: 'ENGINE: eg5564',
                bold: false
              },
              {
                text: 'CHASSIS: ch665545',
                bold: false
              },
              {
                text: 'REGISTRATION #:',
                bold: false
              }
            ],
            [
              {
                text: 'YEAR: 2023',
                bold: false
              },
              {
                text: 'COLOR: RED',
                bold: false
              },
              {
                text: 'MAKE/MODEL: TAYOTA RAV4',
                bold: false
              }
            ],
            [
              {
                text: 'MARKET VALUE: $105,000.00',
                bold: false
              },
              {
                text: 'AMOUNT PLEDGE: $105,000.00',
                bold: false
              },
              {
                text: 'INSURER:',
                bold: false
              }
            ],
            [
              {
                text: 'INSURANCE AMOUNT:',
                bold: false
              },
              {
                text: 'INSURANCE TYPE:',
                bold: false
              },
              {
                text: 'RENEWAL DATE:',
                bold: false
              }
            ],
            [
              {
                text: 'NAME OF VALUATOR:',
                bold: false
              },
              {
                text: 'VALUATION DATE:',
                bold: false
              },
              {
                text: 'VALUATION EXPIRY DATE:',
                bold: false
              }
            ]
          ]
        }
      },
      {
        text: 'Terms and Conditions Of Loan',
        style: 'subheader',
        bold: true,
        margin: [0, 20, 0, 30]
      },
      {
        ol: [
          [
            'Representations and Warranties . On acceptance of the loan, the Borrower is deemed to represent and warrant that:',
            {
              ul: generateListItems([
                [
                  'The Borrower is legally empowered to incur the indebtedness contemplated by the letter agreement, to execute, deliver and perform the Security documents required on the terms and conditions herein and there are no legal or other obligations binding on the Borrower which would in any way restrict the terms and conditions of the letter agreement;'
                ],
                [
                  'To the best of the Borrower’s knowledge, there are no legal or other proceedings pending or threatened before a court or other tribunal which would have a material or adverse effect on the financial condition of the Borrower (except as disclosed to the Credit Union prior to the date hereof) or could adversely affect the rights of the Credit Union;'
                ],
                [
                  'There is nothing that shall occur which would make it impossible for the Borrower to fulfill the Borrower’s obligations to the Credit Union and the securities entered between the Borrower and the Credit Union shall become unconditional and fully effective accordance with their terms unless otherwise agreed by the parties;'
                ],
                [
                  'The Borrower has taken all necessary action to authorize execution and delivery of the Agreement hereunder and no limit on the powers of the Borrower to borrow shall be exceeded as a result of such borrowing;'
                ],
                [
                  'The Borrower agrees that no charge in favour of any person exists over the whole or any part of the assets or undertaking other than any charges in connection with these presents and shall not so exist after the date of this Agreement and the Borrower shall not without the prior written approval of the Lender grant, establish or permit to arise or subsist any charge in favour of any other person;'
                ],
                [
                  'The Borrower agrees that neither the execution nor delivery of this Agreement and the security documentation nor the transaction herein or therein contemplated nor the compliance with the terms and conditions hereof shall contravene any provision of Law, Statute, Decree, Rule or Regulation to which the Borrower is subject or any Judgment, Decree, Franchise, Order or Permit applicable to it; or conflict with or result in any breach of any of the terms, covenants, conditions and provisions or constitute a default under or result in the creation or importation of any lien, security, interest, charge or encumbrance upon any property or assets of the Borrower pursuant to the terms of any deed, agreement, mortgage, deed of trust or other instrument to which it is a party or is subject or by which it may be bound save as disclosed in writing to the Credit Union prior to this date;'
                ],
                [
                  'The Borrower is not in default under any Agreement to which it is a party or by which it may be bound or in respect of the payment or performance of any obligations for borrowed money and no event has occurred or is continuing with respect to the Borrower that would adversely affect the Borrower’s ability to repay the loan hereby created;'
                ],
                [
                  'The Borrower agrees that statements and other documents supplied to the Credit Union by or on behalf of theBorrower are true and correct in all material aspects and fairly represents the financial condition of the Borrower and the results of bona fide projections of its operations for the period covered thereby and such financial statements have been prepared in accordance with the accounting principles generally accepted and applied on a consistent basis;'
                ],
                [
                  'The Borrower agrees that the rights and remedies of the Credit Union in relation to any misrepresentations or breach of warranty on the part of the Borrower shall not be prejudiced by any investigation by or on behalf of the Credit Union or by the execution or performance of this Agreement or by any other act or thing which may be done by or on behalf of the Credit Union in connection with this Agreement and which might apart from this provision prejudice such rights or remedies;'
                ],
                [
                  'The Borrower agrees that no licence, approval or authorization of any governmental department, authority or agency is required in connection with the execution, delivery, performance, validity or enforceability of this Agreement and the security documentation and if so required has been obtained and shall be kept current and in full force and effect; and'
                ],
                [
                  'The Borrower agrees that this Agreement and the security documentation when executed will constitute the legal, valid and binding obligations of the Borrower enforceable in accordance with their respective terms'
                ]
              ]),
              type: 'none'
            }
          ],
          [
            'Default. The Borrower agrees that, notwithstanding anything contained in the letter agreement, upon the happening of any of the following events (each an "Event of Default"), all monies owed by the Borrower to the Credit Union (whether as to principal, accrued interest, costs or otherwise) shall become immediately payable:',
            {
              ol: [
                {
                  ol: generateListItems([
                    [
                      'If the Borrower defaults in making any payment under the loan;'
                    ],
                    [
                      'If the Borrower commits any breach or default in the performance or observance of this or any other agreement between the Borrower and the Credit Union;'
                    ],
                    [
                      'If the Borrower’s application including supporting documents contains any false or untrue statement or information;'
                    ],
                    [
                      'If any representation or warranty made by the Borrower (or which is deemed to have been made by the Borrower) under the letter agreement and supporting documents or any security for the purpose of obtaining the loan, is incorrect or misleading in any material respect as of the date on which it is made or deemed to be made;'
                    ],
                    [
                      'The Borrower becomes unable to pay his/her debts as they fall due or becomes bankrupt;'
                    ],
                    [
                      'The Credit Union believes, in good faith and upon commercially reasonable grounds, that all or part of the property subject to any of the security is or is about to be placed in jeopardy or that a material adverse change in the Borrower’s financial affairs has occurred;'
                    ],
                    [
                      'The holder of a lien or other security interest takes possession of all or part of the Borrower’s property or distress, execution or other similar process is levied against any such property; or'
                    ],
                    [
                      'Any part of the security required by the letter agreement and supporting documents terminates or is no longer in effect without the Credit Union’s written consent.'
                    ]
                  ]),
                  type: 'none'
                }
              ]
            },
            {
              ol: [
                {
                  text: '2.1 The Borrower further agrees that in the event that the Borrower defaults on loan payments then any payment made subsequent to said default shall be allocated firstly to penalty, secondly to interest and thirdly to capital.'
                },
                {
                  text: '2.2 These Events of Default are in addition to, and not in substitution for, any events of default or other events resulting in accelerated repayment contained in any loan or security document made or to be made between the Credit Union and the Borrower in relation to this loan.'
                },
                {
                  text: '2.3 Notice of Default. The Borrower shall promptly notify the Credit Union of the occurrence of any event that is an Event of Default (or any that would be an Event of Default if the only thing required is either notice being given or time elapsing or both).'
                }
              ],
              type: 'none'
            }
          ],
          [
            'Covenants. The Borrower and Guarantor Jointly and Severally covenant and agree that:',
            {
              ul: generateListItems([
                [
                  'The Borrower shall, as from the date hereof and so long as the loan or any part thereof or any interest thereon remains outstanding, inform the Credit Union promptly of the happening of any event likely to have a substantial, adverse effect on the Borrower’s financial position or operations and of any unusual or long term contract likely by reason of its nature or magnitude to materially affect the interest of the Credit Union.'
                ],
                [
                  'The Borrower shall take all reasonable steps to keep all buildings, erections and structures forming part of theimmovable properties which the Borrower owns or occupies and all plant, machinery, fixtures and other effects thereon and therein and all motor vehicles in a good state of repair and in good working order and condition and shall keep all buildings, machinery and motor vehicles adequately insured.'
                ],
                [
                  'The Borrower shall, in accordance with sound practice, insure and keep insured all such parts of the Borrower’s property as are liable and insurable against loss or damage by fire, however caused, hurricane, windstorm, earthquake, riot and strike, flood, however caused, explosion, overflow of the sea, theft, malicious damage and such other risks as the Credit Union may from time to time direct in the name of the Credit Union for a sum not less than the monies hereby secured or such greater sum not exceeding the full replacement cost of the insurable items of the mortgaged property as may be required in such manner as in any case in such insurance office, to be stipulated by the Borrower as the Credit Union may from time to time approve. The monies payable under the insurance effected under this covenant shall be payable in preference and priority to all other insurance now or hereafter to be effected on the mortgaged property AND the Borrower will forthwith and without demand deliver to the Credit Union all such policies of insurance and will punctually pay all premiums of insurance necessary for the purpose of keeping such insurance in force and will deliver to the Credit Union all receipts and vouchers for the payment of the premiums from time to time. The Borrower HEREBY IRREVOCABLY APPOINTS the Credit Union to be its Attorney to ask, demand, sue for recover and receive and give effectual discharges for all monies which may become due and owing or payable to under or in respect of any insurance now or hereafter to be effected on the mortgaged property. Any monies received under the aforesaid insurance shall be applied by the Borrower either in making good the loss or damage in respect of which it was received or at the option of the Credit Union without prejudice to any obligations imposed by this Agreement in or towards discharge of the monies hereby secured. The Borrower shall hold any monies received on such insurance in trust for the Credit Union. If the Borrower shall at any time refuse or neglect to keep the said insurance or to produce and deliver any policy or receipt to the Credit Union in accordance with the Agreement hereinbefore contained, the Credit Union shall be entitled to insure the mortgaged property or any part thereof in the manner hereinafter contained and the Borrower shall on demand repay to the Credit Union every sum expended by the Credit Union in so doing, together with interest thereon from the date of expenditure at the rate of interest from time to time payable in respect of similar loans being offered by the Credit Union'
                ],
                [
                  'So long as the loan package or any part thereof or interest thereon or other sums payable thereunder remainoutstanding, the Borrower shall provide the Credit Union with unaudited financial statements in respect of its business within sixty (60) days of the end of each quarter and with audited financial statements within one hundred and twenty (120) days of the end of their respective financial year'
                ],
                [
                  'Without prejudice to the remedies available to the Credit Union hereunder or under the securities or otherwise if the Borrower fails to make any payment of installment of principal or interest for two or more months (consecutive or combined) to pay a late payment charge of 2% per month on the total arrears beginning the second month and each month thereafter until all such arrears have been paid. So long as such default continues, the Credit Union further reserves the right to publish the Borrower’s name in any periodical newspaper or media.'
                ],
                [
                  'At all reasonable times the Borrower shall permit the Credit Union by its servants, agents or independent contractors to view the state and condition of the property, machinery and equipment of the Borrower relevant to the loan package and all defects and want of reparation in breach of this covenant found upon any such inspection shall be remedied upon notice to the Borrower in writing within thirty (30) days. If there be any default of the Borrower to comply thereto it shall be lawful but not obligatory for the Credit Union to cause all necessary repairs or amendments to be done or effected at the cost of the Borrower and such costs to be repayable on demand and bear interest at the current rate in respect of similar loans being offered by the Credit Union.'
                ],
                [
                  'There shall be no change in the nature of the business or status of the Borrower or assets financed by the loan by reason of any sale, merger or consolidation without the written consent of the Credit Union.'
                ],
                [
                  'The Borrower shall duly and punctually pay or cause to be paid all taxes, assessments, duties and other charges imposed or charged upon its properties and assets which it is liable by Law to pay except that no such tax assessments, duty or charge need to be paid if being contested in good faith and by appropriate proceedings promptly initiated and urgently conducted and if such reserve or other appropriate provisions as required by generally accepted accounting principles shall have been made thereafter'
                ],
                [
                  'The Borrower shall promptly notify the Credit Union of the occurrence of any event or circumstance which afternotice or lapse of time or both, would constitute an Event of Default and of any matter which might materially or adversely affect the financial condition of the business or assets.'
                ],
                [
                  'The Borrower shall pay to the Credit Union on demand at the Head Office of the Credit Union at 66 Slipe Road,Kingston 5, in the parish of St Andrew or such other branch of the Credit Union as the Credit Union may at any time or from time to time notify to the Guarantor all sums of money which now or at any time hereafter may be due or owing by the Borrower to the Credit Union or for which the Guarantor may be or become liable to the Credit Union on any current or other account or in any manner whatever and to discharge all liabilities incurred by the Borrower to the Credit Union in any manner whatever (and in the case of both money owing and liabilities incurred whether alone or jointly with any other person and in whatever style or name and whether such monies are owed or liabilities are incurred as principal or surety for the payment of the indebtedness to the Credit Union of any person, limited company or other corporation, firm, committee, association or other unincorporated body whatsoever) together with interest to the date of payment at the prescribed rate or at such other rate or rates of interest as the Credit Union shall from time to time charge, commission, discount as well as other banking charges, legal and other costs, charges and expenses. The said interest shall be computed daily at such rate or rates as aforesaid (as well after as before any judgment for the recovery of the monies due or owing or for which the Borrower may become liable to the Credit Union as aforesaid or any part thereof) and shall be payable on such day in every month as the Credit Union may from time to time determine. If such interest is not punctually paid for two or more months (consecutive or combined), the Borrower shall pay a penalty of 2% per month on the total arrears beginning the second month and each month thereafter until all such arrears have been paid but without prejudice to the right of the Credit Union to require payment of such interest.'
                ],
                [
                  'The Credit Union shall be at liberty from time to time to give time for payment of any bill or bills of exchange,promissory note or other security or securities which may have been discounted for or received on account of the Borrower by the Credit Union or on which the Borrower shall or may be liable as drawer or endorser or otherwise to any party or parties thereto or liable thereon as the Credit Union shall in its discretion think fit without in any manner releasing the Guarantor or affecting the security hereby made.'
                ],
                [
                  'The Borrower shall permit the Credit Union by its servants or agents to enter any premises or building under thecontrol of the Guarantor in which the said chattels may be for the time being and to view and inspect and take inventories thereof at all reasonable times.'
                ],
                [
                  'The Borrower shall insure and keep insured the said chattels as required by Law and against fire, theft, damage and such other contingencies and risks as the Credit Union shall reasonably require in the name of the Credit Union to the full insurable value thereof or such other amount as may reasonably be required by the Credit Union in some insurance office to be approved by the Credit Union and to make punctually all payments required for keeping such insurance on foot and on demand to produce and deliver up to the Credit Union the said policy of insurance and the receipt for each payment of premium paid in respect thereof. If default shall at any time be made by the Borrower in effecting and keeping up such insurance, it shall be lawful for the Credit Union to insure and keep insured the said chattels as aforesaid and all monies expended by the Credit Union for such purpose together with interest thereon in manner hereinbefore provided from the time of the same having been expended, shall on demand be repaid to the Credit Union by the Guarantor and until such repayment shall be a charge upon the said chattels hereby assigned. THE GUARANTOR HEREBY IRREVOCABLY APPOINTS the Credit Union to be the Attorney for the Borrower, his executors and administrators during the subsistence of this Indenture to ask for, demand, sue for, recover and receive and give effectual discharges for all monies which may become due and owing or payable to, under or in respect of any insurance now or hereafter to be effected on the said chattels or any of them or any chattels and things substituted therefore and without prejudice to the rights conferred upon the Credit Union hereunder or by Law. All such monies shall, if the Credit Union so requires, be applied in or towards repayment of any monies due or owing to the Credit Union and hereby secured.'
                ],
                [
                  'The Guarantor and every other person claiming by or through the Guarantor any interest in the said chattels shall at all times at the sole cost of the Guarantor execute and do all such assurances and things for the future and better assigning of the said chattels unto the Credit Union and enabling the Credit Union to obtain possession of the same as may be by the Credit Union lawfully required.'
                ],
                [
                  'The Borrower shall not permit or allow any writ of fieri facias or other writ, execution or distress for rent rates ortaxes to be levied or taken or distrained upon the said chattels.'
                ],
                [
                  'The Borrower shall not permit or allow the said chattels or any part or parts thereof to be injured or to deteriorate subsequent to the execution of this bill of sale in a greater degree than the same would deteriorate by reasonable use and wear thereof and whenever any part or parts of the said chattels are injured or deteriorated, the Borrower shall act forthwith to replace, repair and make good the same. Any part substituted for a destroyed part shall be deemed to be included in this security.'
                ],
                [
                  'The Borrower shall not remove the said chattels or any part or parts thereof from the place or places where the said property is presently kept or installed without prior consent in writing of the Credit Union.'
                ],
                [
                  'The Borrower shall on demand pay the costs of and incidental to the preparation, stamping and completion of this security and the protection, realization and enforcement thereof and otherwise in relation thereto.'
                ],
                [
                  'That so long as any money shall be owing on the security of these presents notwithstanding that the same may not as yet have become payable, the Credit Union, its servants or agents may, without previous notice to the Guarantor, seize and take possession of the said chattels in whatever place or places they may happen to be.'
                ],
                [
                  'That in exercise of the power to seize the said chattels assigned, the Credit Union, its servants and agents may enter and remain upon any premises where the said chattels may be or believed to be and if necessary may break open doors and windows, gates or fences in order to obtain possession thereof and to seize and take away the same. The Credit Union may, after the expiration of seven clear days from the date of the seizure, sell the said chattels either separately or in lots by public auction or private contract on or off the said premises and retain out of the proceeds, the balance of the monies hereby secured and interest as may then remain unpaid. All costs and expenses incurred, made or sustained in or about entering upon the said premises and in discharging any distress, execution or other encumbrances on the said chattels and seizing, taking, retaining and keeping possession of the said chattels and in and about the removal or sale (including the costs of advertising) of the said chattels shall also be satisfied from the proceeds of sale. After the full payment of all monies then due or owing to the Credit Union have been secured and such costs, charges, payments, expenses and encumbrances as aforesaid shall have been satisfied then the Credit Union shall pay over the surplus (if any) of the proceeds of such sale to the Guarantor.'
                ],
                [
                  'That no neglect, omission or forbearance on the part of the Credit Union to take advantage of or enforce any right or remedy arising out of any breach or non-observance of any covenant or condition herein contained or implied shall be deemed to be or operate as a general waiver of such covenant or condition or the right to enforce or take advantage of the same in respect of any breach or non-observance thereof either original or recurring.'
                ],
                [
                  'So long as any monies due or owing to the Credit Union by the Borrower shall remain unpaid, the Borrower shall keep the said chattels in good repair and running order and complete with all fittings and accessories with which the same were purchased.'
                ],
                [
                  'It shall be lawful for but not obligatory on the Credit Union to advance and pay all monies necessary for remedying any breach or non-observance by the Guarantor of any of the Guarantor’s covenants or stipulations hereunder and all costs, charges and expenses properly incurred hereunder by the Credit Union and all other monies properly in time or respective times of paying or advancing the same and shall be repayable on demand and in the meantime shall be hereby secured.'
                ],
                [
                  'This security shall be in addition and without prejudice to any and every other remedy, lien or security which theCredit Union may or but for the said charge would have for the monies hereby secured or any part thereof.'
                ],
                [
                  'The total amount of the money secured or to be ultimately recoverable hereunder, being uncertain and without any limit, this indenture shall be impressed in the first instance with stamp duty to cover the prescribed amount of money but the Credit Union shall be and is hereby empowered at any time or times hereafter (without further licence or consent of the Guarantor) to impress additional stamp duty hereon covering any sum or sums by which the said indebtedness may exceed the amount of money recoverable.it being the intent of these presents that until its discharge the Mortgage, Bill of Sale or Set Off, whichever is applicable, hereby created shall be a continuing security covering the indebtedness of the Borrower to the Credit Union for such amount of money as the stamp duty impressed hereon will extend to cover.'
                ],
                [
                  'The Credit Union shall not be under any obligation to afford or continue credit or facilities to the Borrower to anyaggregate sum in excess of such limit and extent as the Credit Union may, in its absolute discretion from time to time think fit and the Credit Union may at any time or times require the reduction or discharge of the Borrower’s indebtedness secured by this Bill of Sale and the Guarantor shall be bound to comply immediately with every such requisition or demand.'
                ],
                [
                  'The Credit Union shall not be liable for any involuntary loss which may occur by reason of the exercise or execution (whether contemporaneously or otherwise) of any or all of its rights, remedies and powers conferred, given or implied by this indenture or by Law.'
                ],
                [
                  'Without prejudice to any powers in that behalf hereinbefore contained, the Credit Union shall have power from time to time according to the usual mode of the Credit Union in respect of monies due on current account to vary whether by way of increase or reduction the rate of interest then payable hereunder and the dates on and the periods for which interest shall attract a penalty and the Credit Union shall not be required to give any prior notice whatsoever of any such variation to the Borrower or Guarantor.'
                ],
                [
                  'This security shall not be affected by nor affect any other security which the Credit Union may now or hereafter hold from the Borrower or Guarantor. The Credit Union shall be at liberty to realize the securities in such order and manner and to apply and appropriate any monies at any time or times paid by or on behalf of the Borrower or resulting from a realization of this or any other security or any part thereof to such account or item of indebtedness in such sequence, priority and order as the Credit Union may, in its absolute discretion, from time to time determine, notwithstanding any direction from the Borrower or Guarantor to the contrary.'
                ],
                [
                  'This security shall not be considered as satisfied or discharged by any intermediate payment of the whole or part of the monies owing as aforesaid but shall constitute and be a continuing security to the Credit Union notwithstanding any settlement of account or other matter or thing whatsoever and shall be in addition to any other security or securities which the Credit Union may from time to time hold or take from the Borrower or Guarantor and shall not be affected or prejudiced by or affect or prejudice in any way any other security or securities which the Credit Union may now or hereafter hold or take from the Borrower or Guarantor. d he Credit Union shall be at liberty to realize such securities as it may hold or take from the Borrower or Guarantor in such order and manner and to pay, apply and appropriate any money at any time or times paid, by or on behalf of the Borrower or resulting from a realization of this or any other security or any part thereof to such account (including a new account) or item of indebtedness and in such sequence, priority and order as the Credit Union may in its absolute discretion from time to time determine notwithstanding any direction from the Borrower to the contrary.'
                ],
                [
                  'A demand for payment or any other demand or notice hereunder may be properly and effectually made by any Manager or Assistant Manager of any branch of the Credit Union or by an Agent, Attorney acting on its behalf or Attorney-at-Law of the Credit Union by letter sent by registered post, addressed to the Borrower or Guarantor at the address given in this security or at the last known place of business of the Borrower or Guarantor and every demand so made shall be deemed to have been made on the third day following the day the letter was posted in any post office in Jamaica.'
                ],
                [
                  'Upon payment by the Borrower or Guarantor to the Credit Union of all monies payable under this security, and where applicable, all costs and expenses which it may incur in lawfully seizing and removing the said chattels hereby assigned and any costs which it may in any wise incur in or about the protection, enforcement or realization of this security the Credit Union shall, at the request and cost of the Borrower or Guarantor cause satisfaction to be entered on the margin of the record of these presents.'
                ]
              ]),
              type: 'none'
            }
          ],
          [
            'For the avoidance of doubt, clauses 4(b), (c), (l), (m), (n), (o), (p), (q), (s) (t) and (v) apply only where the security used as collateral for the loan is a combination of real property (land), buildings, erections and chattels upon said land.'
          ],
          [
            'Payments. If any payment is due on a day other than a day on which the Credit Union is open for business (a "Business Day") then the payment is due on the next Business Day.'
          ],
          [
            'Place of Payment. All payments of principal and interest are payable in Jamaican dollars at the offices of the Credit Union as advised by the Credit Union.'
          ],
          [
            'Proof of Debt. This letter agreement provides the proof, between the Credit Union and the Borrower, of the loan made available to the Borrower. There may be times when the Borrower is required to sign additional documents. Throughout the time that the loan is provided by the Credit Union under this letter agreement, the Credit Union’s loan accounting records will provide complete proof of all terms and conditions of the loan (such as principal loan balances, interest calculations and payment dates).'
          ],
          [
            'Security. The securities held by the Credit Union under the letter agreement together with any other securities which are now held or hereafter may be held by the Credit Union, shall be continuing securities notwithstanding any settlement of account or other matter whatsoever, and shall secure all monies obligations and liabilities whether actual or contingent now or hereafter due owing or incurred to the Credit Union and accordingly the Credit Union is hereby (and by the terms of securities shall be) empowered at any time or times (without further license or consent from the Borrower) to impress the said securities with additional Stamp Duty to cover any sum by which the total indebtedness of the Borrower may exceed the amounts stated in this letter agreement or in the said security.',
            {
              ul: [
                [
                  '8.1 These securities shall be valid, binding and enforceable against the Borrower and/or other security party.'
                ]
              ]
            }
          ],
          [
            'Costs and fees. All stamp duties, Attorney’s fees, registration fees and any other fees, costs or charges in connection with the loan and the preparation or enforcement of any document or security related to the loan will be for the Borrower’s account. If for any reason the transactions contemplated by this letter agreement are not completed, the amount incurred by the Credit Union shall constitute a debt owing by the Borrower to the Credit Union payable on demand.'
          ],
          [
            'Prepayment. Unless otherwise agreed, the Borrower may prepay all or part of the loan at any time without notice or penalty.'
          ],
          [
            'Indemnity. The Borrower will indemnify and hold the Credit Union and its officers, directors, affiliates, employees, attorneys, contractors and agents (each, an "Indemnified Party") harmless from and against any and all claims, liabilities, losses, damages, costs and expenses, including without limitation, reasonable attorney’s fees and disbursements, other dispute resolution expenses (including fees and expenses in preparation for a defense of any investigation, litigation or proceeding) and costs of collection that arise out of or in connection with (i) the Borrower’s breach of any obligation under the letter agreement or (ii) the Credit Union’s acting in accordance with the Borrower’s instructions or orders, except to the extent such claim, liability, loss, damage, cost or expense is found in a final, non-appealable judgment by a court of competent jurisdiction to have resulted from such Indemnified Party’s gross negligence or willful misconduct.'
          ],
          [
            'Waiver. No failure or delay by the Credit Union in exercising any power or remedy shall operate as a waiver thereof nor shall any single or partial exercise or waiver of any right power or remedy preclude its further exercise or the exercise of any other right, power or remedy. Union shall also be entitled at its option and without notice to combine, consolidate, or offset balances on any accounts of the Borrower. The Borrower hereby irrevocably authorizes the Credit Union to debit any account which the Borrower may have with the Credit Union in connection with this letter agreement. The Credit Union’s rights under this section are in addition to other rights and remedies (including other rights of set-off) which the Credit Union may have under this letter agreement or applicable law.'
          ],
          [
            'Set Off. The Borrower agrees that in addition to any or similar right to which the Credit Union may be entitled, the Credit'
          ],
          [
            'No Deduction or Withholding. Payments of principal and interest shall be made without any deduction or set off whatsoever. If the Borrower is obliged by any law enacted after the date of this letter agreement to deduct any taxes or other amounts from principal or interest then unless otherwise provided herein to the contrary, the amount payable by the Borrower shall be increased to the extent necessary to ensure that the net amount actually received by the Credit Union is equivalent to the amount which the Credit Union would have received had there been no obligation to make such deduction or withholding. In the event that the Credit Union is required to pay any such taxes (save and except income tax) then the Borrower shall reimburse same to the Credit Union on demand.'
          ],
          [
            'Increased Costs. If due to the introduction of, or any change in, or in the interpretation of any law or regulation, or the compliance with any guideline or request from the regulators or other governmental authority, there shall be any increase in the cost of the Credit Union agreeing to make or making, funding or maintaining the loan amount then the Borrower shall upon demand from the Credit Union pay the Credit Union the additional amounts sufficient to compensate the Credit Union for such increased cost.'
          ],
          [
            'Force Majeure. The Credit Union shall not be responsible for any failure to perform any of its obligations under the letter agreement if such performance would result in it being in breach of any law, regulation or other requirement of any governmental or other authority in accordance with which it is required to act or if its performance is prevented, hindered or delayed by a Force Majeure Event (as defined below). In such case, its obligations shall be suspended for so long as the Force Majeure Event continues. "Force Majeure Event" means any event due to any cause beyond the reasonable control of the Credit Union, such as restrictions on convertibility or transferability, requisitions, involuntary transfers, sabotage, fire, flood, explosion, acts of God, civil commotion, strikes or industrial action of any kind, riots, insurrection, war or acts of government or similar institutions, failure of the Internet, epidemic, pandemic or other localized or global public health emergency.'
          ],
          [
            'Liability. Under no circumstances shall the Credit Union be liable for indirect, incidental or consequential loss or damages in connection with the loan even if advised of the possibility of such loss or damage.'
          ],
          [
            'Assignment. This letter agreement is binding upon and will ensure to the benefit of the Credit Union and its successors and assigns. Provided always that this Agreement is not assignable by the Borrower.'
          ],
          [
            'Notices. Every notice or other communication under this letter agreement shall be in writing and shall be deemed to have been received by the addressee if sent by registered mail five (5) days after posting or if delivered personally or sent by facsimile at the time of delivery or dispatch if same occurs during normal business hours on a business day but otherwise at the opening of business on the next succeeding business day.'
          ],
          [
            'Governing Law & Jurisdiction. This letter agreement shall be governed by and construed in accordance with the laws of Jamaica and each party unconditionally submits to the jurisdiction of the Jamaican Courts.'
          ],
          [
            'Material Adverse Clause: The Credit Union reserves the right to call the loan where there is evidence of a Material Adverse Condition. A Material Adverse Condition is evidenced by one or more of the following conditions:'
          ]
        ]
      },
      {
        ul: [
          [
            'Material reduction in security margins or values, including minimum credit balances, where applicable.'
          ],
          [
            'Significant deterioration in or development of factors that are likely to substantially impair the ability of the Borrower to repay the loan as evidenced by one or more of the following: cash flows and tangible net worth.'
          ]
        ],
        pageBreak: 'after'
      },
      {
        ol: [
          {
            text: 'I hereby declare that I;',
            margin: [0, 0, 0, 10]
          },
          {
            ol: [
              ['I have received and read this Agreement;'],
              ['I understand the terms and conditions contained herein; and'],
              [
                'Understand that I am bound to all the terms and condition included herein;'
              ],
              [
                'The Agreement has been explained to me by the in the language understood by us and I have understood the entire meaning of the various terms and condition stated in the Agreement.'
              ],
              ['I have signed this Agreement after understanding its content.']
            ],
            margin: [0, 0, 0, 40]
          }
        ]
      },
      {
        table: {
          widths: ['65%', '35%'],
          body: [
            [
              {
                text: 'SIGNED by MARCIA BROWN-ELLIOTT, MEMBER',
                bold: false,
                margin: [0, 0, 0, 20]
              },
              {}
            ],
            [
              {
                text: [
                  'before me: -',
                  {
                    text: ' ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ,',
                    decoration: 'underline'
                  },
                  'Witness'
                ],
                margin: [0, 0, 0, 20]
              },
              {
                table: {
                  body: [
                    [
                      {
                        text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ ',
                        decoration: 'underline'
                      }
                    ],
                    ['Signature of Member']
                  ]
                },
                layout: {
                  hLineWidth: function (i, node) {
                    return 0;
                  },
                  vLineWidth: function (i, node) {
                    return 0;
                  }
                }
              }
            ],
            [
              {
                text: [
                  'at',
                  {
                    text: ' ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                    decoration: 'underline'
                  },
                  'in the parish of',
                  {
                    text: ' ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ ',
                    decoration: 'underline'
                  }
                ],
                margin: [0, 0, 0, 20]
              },
              {
                text: 'Date :'
              }
            ],
            [
              {
                margin: [0, 0, 0, 0],
                table: {
                  widths: ['*'],
                  body: [
                    [
                      {
                        text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                        decoration: 'underline'
                      }
                    ],
                    [
                      {
                        columns: [
                          {
                            text: 'Signature of Witness'
                          },
                          {
                            text: 'Date',
                            alignment: 'right',
                            margin: [0, 0, 30, 0]
                          }
                        ]
                      }
                    ]
                  ]
                },
                layout: {
                  hLineWidth: function (i, node) {
                    return 0;
                  },
                  vLineWidth: function (i, node) {
                    return 0;
                  },
                  paddingLeft: function (i, node) {
                    return 0;
                  },
                  paddingRight: function (i, node) {
                    return 0;
                  },
                  paddingTop: function (i, node) {
                    return 0;
                  },
                  paddingBottom: function (i, node) {
                    return 0;
                  }
                }
              },
              {}
            ]
          ]
        },

        layout: {
          hLineWidth: function (i, node) {
            return i === 0 || i === node.table.body.length ? 0.5 : 0;
          },
          vLineWidth: function (i, node) {
            return 0.5;
          }
        }
      }
    ]
  ];

  const motorVehicleBlock = [
    {
      text: 'LOAN AGREEMENT',
      bold: true,
      alignment: 'center',
      margin: [0, 0, 0, 20]
    },
    {
      text: [
        'THIS INSTRUMENT is made on the',
        {
          text: ` ${today.day} `,
          bold: true
        },
        'day of',
        {
          text: ` ${today.month} ${today.year} `,
          bold: true
        },
        'BETWEEN the party set out at',
        {
          text: ' ITEM 1 ',
          bold: true
        },
        'of the',
        {
          text: ' FIRST SCHEDULE ',
          bold: true
        },
        'hereto (hereinafter referred to as “the BORROWER”) which expression where the context so admits shall include his/her Heirs, successors, executors assigns and Personal Representatives, the party set out at',
        {
          text: ' ITEM 2 ',
          bold: true
        },
        'of the',
        {
          text: ' FIRST SCHEDULE ',
          bold: true
        },
        'hereto (hereinafter referred to as “the GUARANTOR”) which expression where the context so admits shall include his/her Heirs, successors, executors assigns, heirs and Personal Representatives; and',
        {
          text: ' COK SODALITY COOPERATIVE CREDIT UNION LIMITED ',
          bold: true
        },
        'a Co-operative Society duly incorporated under the Co-operative Society’s Act of Jamaica with Registered Office at',
        {
          text: ' 66 Slipe Road, Kingston 5 ',
          bold: true
        },
        'in the parish of',
        {
          text: ' Saint Andrew ',
          bold: true
        },
        '(hereinafter referred to as “COK SODALITY”) which expression shall include its successors and assigns.'
      ],
      margin: [0, 0, 0, 10]
    },
    {
      table: {
        body: [
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'This document shall be referred to as the LOAN AGREEMENT.',
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'COK SODALITY has offered to the BORROWER the sum set out at ITEM 3 of the FIRST SCHEDULE (hereinafter the ‘LOAN SUM’)',
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'The LOAN SUM is to be repaid along with interest at the rate of interest set out at ITEM 4 of the FIRST SCHEDULE hereto (hereinafter referred to as ‘COK INTEREST RATE’).',
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'COK SODALITY has agreed to Lend subject to the terms and conditions hereinafter contained and the terms and conditions of the Offer Letter by COK SODALITY particularized at ITEM 5 of the FIRST SCHEDULE (hereinafter the ‘OFFER LETTER’) and also the Bill of Sale further particularized at ITEM 2.1.3.',
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'The Offer Letter and the Bill of Sale shall be read together with this Agreement in any event for litigation purposes or otherwise.',
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'The GUARANTOR undertakes certain payment obligations to COK SODALITY under the Agreement, and the BORROWER has requested that the GUARANTOR guaranty the payment obligations as an inducement to COK SODALITY to enter into the Agreement with the BORROWER.',
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'The purpose of the loan is for the Purchasing of the CHATTEL particularized in the SECOND SCHEDULE (hereinafter the ‘MOTOR VEHICLE’) and things more particularly mentioned and described in the Second Schedule hereto (hereinafter referred to as “the CHATTEL”).',
              bold: false
            }
          ],
          [
            {
              text: 'WHEREAS',
              bold: false
            },
            {
              text: 'COK SODALITY is relying on information in deciding to grant the loan request. Both the GUARANTOR and the BORROWER represent, warrant and certify that the information provided in all documents submitted as security for the loan as true, correct and complete.',
              bold: false
            }
          ]
        ]
      },
      layout: {
        hLineWidth: function (i, node) {
          return 0;
        },
        vLineWidth: function (i, node) {
          return 0;
        },
        paddingBottom: function (i, node) {
          return 5;
        }
      },
      pageBreak: 'after'
    },
    {
      ol: generateNumberedList([
        [
          'AVAILABILITY CONDITIONS',
          {
            ol: [
              [
                'LOAN SUM will be made available for disbursement to the BORROWER:',
                {
                  ol: [
                    [
                      'On compliance with the terms and conditions of the Offer Letter; and'
                    ],
                    [
                      'upon completion of all other related documents; and any security documentation as COK SODALITY and its Attorney(s) in their sole discretion may deem necessary for the transaction'
                    ],
                    [
                      'payment of the relevant commitment fees, legal fees and stamp duties associated with the preparation, stamping and registration of this and any other security documentation together with General Consumption Tax (GCT) as applicable.'
                    ]
                  ]
                }
              ],
              [
                'The representations and warranties set forth herein shall be true on and as of the date of disbursement with the same effect as though such representations and warranties had been made on and as of the date of such disbursement.'
              ],
              [
                'The securities shall have been entered into between the respective parties thereto and shall each become unconditional and fully effective in accordance with their terms.'
              ],
              [
                'The BORROWER shall disclose to COK SODALITY:',
                {
                  ol: [
                    [
                      'anything which has happened which may:',
                      {
                        ol: [
                          [
                            'materially or adversely affect:',
                            {
                              ol: [
                                ['the carrying out of the undertaking; or'],
                                ['the business prospects; or'],
                                ['financial conditions of the BORROWER; or']
                              ]
                            }
                          ],
                          [
                            'make it difficult for the BORROWER be able to fulfil obligations:',
                            {
                              ol: [
                                ['to COK SODALITY hereunder; or'],
                                ['under any securities.']
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      'if the BORROWER has incurred any material loss or liability.'
                    ]
                  ]
                }
              ]
            ]
          }
        ],
        [
          'SECURITY',
          {
            ol: [
              [
                'As security for the Repayment of the loan; payment of interest; and all other amounts owing by the BORROWER to COK SODALITY under these presents or under any of the securities, the BORROWER has issued; shall issue; or cause to be issued, to COK SODALITY the following: -',
                {
                  ol: [
                    [
                      'A Promissory Note from the Borrower in the amount particularized at ITEM 6 of the FIRST SCHEDULE(hereinafter the ‘PROMISSORY NOTE AMOUNT’).'
                    ],
                    [
                      'Standard Cuna Mutual Loan Protection Insurance covering the sum particularized at ITEM 7 of the FIRST SCHEDULE (hereinafter the ‘STANDARD CUNA COVERAGE AMOUNT’) of total loan amount held with COK SODALITY'
                    ],
                    [
                      'Bill of Sale registered and stamped over the MOTOR VEHICLE valued by the company particularized at ITEM 8 of the FIRST SCHEDULE (hereinafter the ‘VALUATION COMPANY’) on the date particularized at ITEM 9 of the FIRST SCHEDULE (hereinafter the ‘VALUATION DATE’) which was assessed at the MARKET VALUE particularized at ITEM 10 of the FIRST SCHEDULE (hereinafter the ‘MARKET VALUE’).'
                    ],
                    [
                      'Comprehensive Insurance by a reputable Insurance Company on motor vehicle with COK SODALITY’sInterest noted.'
                    ],
                    [
                      'Any other security documents mentioned in the OFFER LETTER.'
                    ],
                    [
                      'Any other security documents mentioned in the OFFER LETTER particularized at ITEM 11 of the FIRST SCHEDULE (hereinafter the ‘OTHER SECURITY’).'
                    ]
                  ]
                }
              ],
              [
                'The aforementioned securities in Clause 2.1 shall be valid, binding and enforceable against the BORROWER and/or other security party.'
              ]
            ]
          }
        ],
        [
          'REPRESENTATION AND WARRANTIES',
          {
            ol: [
              [
                'The BORROWER represents and warrants to and undertakes with COK SODALITY that: -',
                {
                  ol: [
                    [
                      {
                        text: 'THE POWERS OF THE BORROWER',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'BORROWER has full power to:',
                            {
                              ol: [
                                [
                                  'enter into the obligations contained herein; and'
                                ],
                                ['incur the loan contemplated herein; and'],
                                [
                                  'execute deliver and perform this Agreement and any Security documents required, on the terms andconditions herein and therein contained.'
                                ]
                              ]
                            }
                          ],
                          [
                            'The BORROWER has taken all necessary action to authorize execution and delivery of this Agreementhereunder and no limit on the powers of the BORROWER to borrow will be exceeded as a result of such borrowing.'
                          ],
                          [
                            'Neither the execution nor delivery of this Agreement and the Security Documentation nor the transactionherein or therein contemplated nor the compliance with the terms and conditions hereof will:'
                          ],
                          [
                            'contravene any:',
                            {
                              ol: [
                                ['provision of Laws statute decree rule;'],
                                [
                                  'regulation to which the BORROWER is subject;'
                                ],
                                ['judgment decree franchise order; or'],
                                ['permit applicable to it.']
                              ]
                            }
                          ],
                          [
                            'conflict with or result in any breach: of any of the terms; covenants; conditions; provisions; constitute a default under; result in the creation; or importation of any lien, security, interest, charge or encumbrance upon any of the property or assets of the BORROWER pursuant to the terms of any deed agreement mortgage deed of trust or other instrument to which it is: a party; is subject; or by which it may be bound, save as disclosed in writing to COK SODALITY prior to the date of this Agreement.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NO CHARGE',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'No charge in favour of any person exists:',
                            {
                              ol: [
                                [
                                  'over the whole or any part of the assets; or'
                                ],
                                [
                                  'undertaking other than any charges in connection with these presents currently exists; and'
                                ],
                                [
                                  'will not so exist after the date of this Agreement.'
                                ]
                              ]
                            }
                          ],
                          [
                            'The BORROWER will not without the prior written approval of COK SODALITY grant or establish or permit to arise or subsist any charge in favour of any other person.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NO DEFAULT IN ANY OTHER PAYMENTS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'The BORROWER is not in default under any Agreement to which the BORROWER is a party or by which the BORROWER may be bound in respect to:',
                            {
                              ol: [
                                ['the payment; or'],
                                [
                                  'performance of any obligations for borrowed money.'
                                ],
                                [
                                  ' No event has occurred or is continuing with respect to the BORROWER that would adversely affect the BORROWER’s ability to repay the loan hereby created.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NO OTHER PROCEEDINGS AGAINST BORROWER',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'No litigation or administrative proceeding is presently:'
                          ],
                          ['in course; or'],
                          ['to the knowledge of the BORROWER pending; or'],
                          [
                            'threatened or which would have a material or adverse effect on the financial condition of the BORROWER except as disclosed to the LENDER in writing prior to the date hereof.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'ACCURACY OF FINANCIAL STATEMENTS AND DOCUMENTS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'The financial statements and other documents supplied to COK SODALITY by or on behalf of the BORROWER is:',
                            {
                              ol: [
                                [
                                  'true and correct in all material respects; and'
                                ],
                                [
                                  'fairly represents the financial condition of the BORROWER; and'
                                ],
                                [
                                  'the result of bona fide projections of the BORROWER’s operations for the period covered; and'
                                ],
                                [
                                  'financial statements prepared in accordance with the accounting principles generally accepted and applied on a consistent basis.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NO PREJUDICE TO RIGHTS & REMEDIES AVAILABLE TO COK SODALITY',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'The rights and remedies of COK SODALITY in relation to any misrepresentations or breach of warranty on the part of the BORROWER shall not be prejudiced by:',
                            {
                              ol: [
                                [
                                  'any investigation by or on behalf of COK SODALITY into the affairs of the BORROWER; or'
                                ],
                                [
                                  'the execution or performance of this Agreement; or'
                                ],
                                [
                                  'any other act or thing, which may be done by or on behalf of COK SODALITY in connection with this Agreement and which might apart from this provision prejudice such rights or remedies.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'APPROVALS OF AUTHORITIES AND AGENCIES',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'Any necessary approval or authorization from any government department authority or agency required in connection with the execution, delivery, performance, validity, or enforceability of this Agreement and the security documentations:'
                          ],
                          ['has been obtained; and'],
                          ['will be kept current, in full force and effect.'],
                          [
                            'This Agreement constitutes and the security documentation, when executed, will constitute the legal, valid and binding obligations of the BORROWER enforceable in accordance with their respective terms.'
                          ]
                        ]
                      }
                    ]
                  ]
                }
              ]
            ]
          }
        ],
        [
          {
            text: 'COVENANTS',
            style: 'subheader'
          },
          {
            ol: [
              [
                'The BORROWER and the GUARANTOR Jointly and Severally covenant and agree that: -',
                {
                  ol: [
                    [
                      {
                        text: 'USE OF LOAN SUM:',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'the LOAN SUM shall be used for the purpose set out in the Offer Letter; and'
                          ],
                          [
                            'the LOAN SUM shall not be used for any purpose except the Purpose in the Offer Letter; and'
                          ],
                          [
                            'failure to comply with clauses 4.1.1.1 and 4.1.1.2 shall not prejudice any rights of COK SODALITY; and'
                          ],
                          [
                            'COK SODALITY shall not be responsible for monitoring or ensuring the use or application by the BORROWER of the LOAN SUM.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'REPAYMENT OF LOAN',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'LOAN SUM and interest shall be payable:',
                            {
                              ol: [
                                ['in monthly instalments; and'],
                                [
                                  'in full, without any set-off or counterclaim; and'
                                ],
                                [
                                  'commencing one calendar month after the date of disbursement of the LOAN SUM.'
                                ]
                              ]
                            }
                          ],
                          [
                            'Punctual payment shall be made to COK SODALITY of each monthly instalment due and payable until the LOAN SUM is fully repaid; and'
                          ],
                          [
                            'COK SODALITY reserves the right to withdraw any shortfall from any savings subaccount maintained by the BORROWER to offset arrears; in the event the BORROWER makes monthly payments, which fall short of the prescribed monthly amount. COK SODALITY may appropriate such payment towards such of the obligations of the BORROWER as COK SODALITY may decide. The BORROWER waives any right to make an appropriation in respect of a partial payment. Any appropriation by COK SODALITY shall apply to the exclusion of any actual or purported appropriation by the BORROWER.'
                          ],
                          [
                            'payment shall be made to COK SODALITY, on demand of all sums of money:',
                            {
                              ol: [
                                [
                                  'which now or at any time hereafter may be due or owing by the BORROWER to COK SODALITY; or for which the GUARANTOR may be or become liable to COK SODALITY on any current or other account or in any manner whatever; and'
                                ],
                                [
                                  'to discharge all liabilities incurred by the BORROWER to COK SODALITY any manner whatever (and in the case of both money owing and liabilities incurred whether alone or jointly with any other person and in whatever style or name and whether such moneys are owed or liabilities are incurred as principal or surety for the payment of the indebtedness to COK SODALITY of any person, limited company or other corporation, firm, committee, association or other unincorporated body whatsoever).'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'COK INTEREST',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'All outstanding sums including any commission, discount and other banking charges, legal and other costs, charges and expenses shall be repaid to COK SODALITY with interest to the date of payment.'
                          ],
                          [
                            'Interest shall be:',
                            {
                              ol: [
                                [
                                  'computed at the rate particularized as COK INTEREST RATE or at such other rate or rates of interest as COK SODALITY shall from time to time charge; and'
                                ],
                                [
                                  'computed daily (as well after as before any judgment for the recovery of the moneys due or owing or for which the BORROWER may become liable to COK SODALITY as aforesaid or any part thereof); and'
                                ]
                              ]
                            }
                          ],
                          [
                            ' payable on such day in every month as COK SODALITY may from time to time determine.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'LATE PENALTY',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'A late penalty of 2% per month on the total arrears of monthly instalments shall be payable on any sum not punctually paid on the due date for two or more months (consecutive or combined).'
                          ],
                          [
                            'Late penalties shall accrue from the second month and each month thereafter until all arrears have been paid but without prejudice to the any remedies available to COK SODALITY hereunder or under any securities or otherwise.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'FEES AND EXPENSES',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'On demand, to pay any fees of and incidental to the preparation, stamping and completion of this security and the protection realization and enforcement thereof and otherwise in relation thereto.'
                          ],
                          [
                            'The BORROWER shall also pay such commitment or processing fees as may be levied by the LENDER from time to time.'
                          ],
                          [
                            'Payment shall be made, on demand and on a full indemnity basis, to COK SODALITY the amount of all costs and expenses (including but not limited to legal fees, professional fees, statutory costs [including stamp duty and registration fees] and out-of-pocket expenses) which COK SODALITY incurs in connection with the preparation, negotiation, execution and delivery of this Agreement and any Related Document.'
                          ],
                          [
                            'Payment shall be made, on demand and on a full indemnity basis, to COK SODALITY all costs and expenses (including but not limited to legal fees, professional fees, statutory costs [including stamp duty and registration fees] and out-of-pocket expenses) incurred by COK SODALITY in connection with any actual or proposed amendment or extension of or any waiver or consent under this Agreement and in contemplation of or otherwise in connection with the enforcement (or attempted enforcement) of, or preservation (or attempted preservation) of any rights under, this Agreement and/or any related document or otherwise in respect of any moneys from time to time owing under this Agreement.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'RIGHT TO PUBLISH',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'So long as there is a default in payment of any sums owed by the BORROWER, COK SODALITY further reserves the right to publish the BORROWER ’s and the GUARANTOR’s name, address, occupation and any other details which COK SODALITY may see fit in any periodical newspaper or media in any manner COK SODALITY sees fit.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'CONCLUSIVE EVIDENCE',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'Any statement of an officer of COK SODALITY as to the amount remaining unpaid to COK SODALITY any time by the Borrower shall be conclusive evidence and shall, in any event, be prima facie evidence as to the amount remaining unpaid to COK SODALITY at such time by the Borrower.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'LENDER’S RIGHT TO VARY TIME AND INTEREST',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'COK SODALITY shall be at liberty from time to time to give time:',
                            {
                              ol: [
                                [
                                  'for payment of any bill; bills of exchange; promissory note; or other security; or securities which may have been discounted for or received on account from the BORROWER by COK SODALITY or on which the BORROWER shall or may be liable as drawer or endorser or otherwise to any party or parties thereto or liable thereon as COK SODALITY shall in its discretion think fit without in any manner releasing the GUARANTOR or affecting the security hereby made.'
                                ],
                                [
                                  'That without prejudice to any of the COK SODALITY’s other powers, COK SODALITY shall have power from time to time in respect of moneys due to vary whether; and'
                                ],
                                [
                                  'by way of increase or reduction the RATE OF INTEREST then payable hereunder; and'
                                ],
                                [
                                  'the dates on and the periods for which unpaid interest shall attract a penalty; and'
                                ],
                                [
                                  'COK SODALITY shall not be required to give any prior notice whatsoever of any such variation to the BORROWER or the GUARANTOR.'
                                ],
                                [
                                  'COK SODALITY shall not be required to give any prior notice whatsoever of any variation to the BORROWER or the GUARANTOR.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'DUTY TO PAY TAXES AND OTHER CHARGES',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'The BORROWER will duly and punctually pay or cause to be paid all taxes, assessments, duties and other charges imposed or charged upon any properties and assets which the BORROWER is liable by Law to pay except that no such tax assessments duty or charge need to be paid if being contested in good faith and by appropriate proceedings promptly initiated and urgently conducted and if such reserve or other appropriate provisions as required by generally accepted accounting principles shall have been made thereafter.'
                          ],
                          [
                            'The BORROWER shall not permit or allow any writ of Fieri Facias or other writ, execution or distress for rent rates or taxes to be levied or taken or distrained upon the CHATTEL.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'ADVANCE PAYMENTS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'It shall be lawful for but not obligatory for, COK SODALITY:',
                            {
                              ol: [
                                [
                                  'to advance and pay all moneys necessary for remedying any breach or non-observance by the GUARANTOR or the BORROWER of any of the GUARANTOR’s or the BORROWER’s covenant’s or stipulations hereunder and all costs, charges and expenses properly incurred hereunder by COK SODALITY and all other moneys properly in time or respective times of paying or advancing the same; and'
                                ],
                                [
                                  'any sum so advanced or paid shall be treated as an addition to the LOAN SUM and the BORROWER hereby irrevocably authorizes COK SODALITY to do so; and'
                                ],
                                [
                                  'any such sum shall be repayable on demand and in the meantime shall be hereby secured; and'
                                ],
                                [
                                  'this security shall be in addition and without prejudice to any and every other remedy, lien or security which COK SODALITY may or but for the said charge would have for the moneys hereby secured or any part thereof.'
                                ]
                              ]
                            }
                          ],
                          [
                            'The total amount of the money secured or to be ultimately recoverable hereunder begin uncertain and without any limit this indenture shall be impressed in the first instance with stamp duty, where applicable, to cover the LOAN SUM.'
                          ],
                          [
                            ' Notwithstanding Clause 4.1.10.2 COK SODALITY shall be and is hereby empowered at any time or times hereafter (without further licence or consent of the GUARANTOR or the BORROWER) to impress additional stamp duty hereon covering any sum or sums by which the said indebtedness may exceed the LOAN SUM it being the intent of these presents that until its discharge the Mortgage, Bill of Sale or Set Off, whichever is applicable, hereby created shall be a continuing security covering the indebtedness of the BORROWER to COK SODALITY for such amount of money as the stamp duty impressed hereon will extend to cover.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NO OBLIGATION TO LEND',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'COK SODALITY shall not be under any obligation to afford or continue credit or facilities to the'
                          ],
                          [
                            'BORROWER to any aggregate sum in excess of such limit and extent as COK SODALITY may, in its absolute discretion, from time to time think fit.'
                          ],
                          [
                            'COK SODALITY may at any time or times require the reduction or discharge of the BORROWER’s indebtedness secured by these presents and securities and the GUARANTOR shall be bound to comply immediately with every such requisition or demand.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'DUTY TO INSURE',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'In accordance with sound practice, to insure comprehensively and keep insured all such parts of its property as are liable to and insurable against loss or damage by:',
                            {
                              ol: [
                                [
                                  'fire, however caused, hurricane, windstorm, earthquake, riot and strike, flood, however caused, explosion, overflow of the sea, theft, malicious damage; and'
                                ],
                                [
                                  'such other risks as COK SODALITY may from time to time direct in the name of COK SODALITY.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      'The CHATTEL shall be insured:',
                      {
                        ol: [
                          [
                            'for a sum not less than the monies hereby secured or for such greater sum not exceeding the full replacement cost of the insurable ITEMs of the CHATTEL property as may be required; and'
                          ],
                          [
                            'in such manner as in any case in such insurance office to be stipulated by the BORROWER, as COK SODALITY may from time to time approve, and'
                          ],
                          [
                            'the monies payable under the insurance effected under this covenant shall be payable in preference and priority to all other insurance now or hereafter to be affected on the CHATTEL.'
                          ]
                        ]
                      }
                    ],
                    [
                      'The BORROWER will forthwith and without demand deliver to COK SODALITY all:',
                      {
                        ol: [
                          [
                            'such policies of insurance and will punctually pay all premiums of insurance necessary for the purpose of keeping such insurance in force; and'
                          ],
                          [
                            'receipts and vouchers for the payment of the premiums from time to time.'
                          ]
                        ]
                      }
                    ],
                    [
                      'The BORROWER HEREBY IRREVOCABLY APPOINTS COK SODALITY to be its Attorney to: ask, demand, sue, recover, receive and give effectual discharges for all monies which may become due and owing or payable to under or in respect of any insurance now or hereafter to be affected on the CHATTEL.'
                    ],
                    [
                      'Any monies received under the aforesaid insurance shall be applied by the BORROWER either:',
                      {
                        ol: [
                          [
                            'in making good the loss or damage in respect of which it was received or at the option of COK SODALITY without prejudice to any obligations imposed by this Agreement) in; or'
                          ],
                          [
                            'towards discharge of the monies hereby secured and the BORROWER shall hold any monies received on such insurance in trust for COK SODALITY.'
                          ]
                        ]
                      }
                    ],
                    [
                      'the BORROWER shall at any time refuse or neglect to:',
                      {
                        ol: [
                          ['keep the said insurance; or'],
                          [
                            'produce and deliver any policy or receipt to COK SODALITY in accordance with the Agreement hereinbefore contained then COK SODALITY shall be entitled to insure the CHATTEL or any part thereof in the manner hereinafter contained and the BORROWER will on demand repay to COK SODALITY every sum expended by COK SODALITY in so doing together with interest thereon from the date of expenditure at the rate of interest from time to time payable in respect of similar loans being offered by COK SODALITY.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'AUDITED AND UNAUDITED FINACIAL STATEMENTS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'If the BORROWER is a corporate body or partnership or self-employed then so long as the LOAN SUM or any part thereof or interest thereon or other sums payable thereunder remain outstanding the BORROWER will provide COK SODALITY with:'
                          ],
                          [
                            'unaudited financial statements in respect of its business within sixty (60) days of the end of each quarter; and'
                          ],
                          [
                            'audited financial statements within one hundred and twenty (120) days of the end of their respective financial year.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NO DESTRUCTION OR DETERIORATION OF CHATTEL',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'So long as any moneys are due or owing:',
                            {
                              ol: [
                                [
                                  'the CHATTEL or any part thereof, shall not be permitted or allowed to be: dismantled; injured; damaged; damaged; malfunctioned or deteriorated subsequent to the execution of these Presents in a greater degree than the same would deteriorate by reasonable use and wear thereof; and'
                                ],
                                [
                                  'whenever any part or parts of the CHATTEL are injured, damaged or deteriorated forthwith to replace, repair and make good the same. Any part substituted for a destroyed; damaged; deteriorated or malfunctioned part shall be deemed to be included in this security.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'RIGHT OF ENTRY',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'The GUARANTOR and BORROWER shall, as may be demanded by COK SODALITY from time to time:'
                          ],
                          [
                            'permit the entry of COK SODALITY by its servants or agents to enter any premises or building in which the CHATTEL may be for the time being and to view and inspect and take inventories thereof; or'
                          ],
                          [
                            'make the CHATTEL available for viewing; inspection or the taking of inventories thereof by COK SODALITY at a location specified by COK SODALITY.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'POWER TO SEIZE',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'That so long as any money shall be owing on any security of these presents notwithstanding that the same may not as yet have become payable, COK SODALITY its servants or agents may, without previous notice to the GUARANTOR, seize and take possession of the CHATTEL in whatever place or places they may happen to be.',
                            {
                              ol: [
                                [
                                  'That in exercise of the power to seize the CHATTEL assigned, the LENDER, its servants and agents:',
                                  {
                                    ol: [
                                      [
                                        'may enter and remain upon any premises where the CHATTEL may be or believed to be and if necessary, may break open doors and windows, gates or fences in order to obtain possession thereof and to seize and take away the same; and'
                                      ],
                                      [
                                        'after the expiration of seven clear days from the date of the seizure, may sell the CHATTEL either separately or in lots by public auction or private contract on or off the said premises and retain out of the proceeds the balance of the moneys hereby secured and interest as may then remain unpaid and all costs and expenses incurred, made or sustained in or about entering upon the said premises and in discharging any distress, execution or other encumbrances on the CHATTEL and seizing, taking, retaining and keeping possession of the CHATTEL and in and about the removal or sale (including the costs of advertising) of the CHATTEL and from and after the full payment of all moneys then due or owing to the LENDER and such costs, charges, payments, expenses and encumbrances as aforesaid shall pay over the surplus (if any) of the proceeds of such sale to the GUARANTOR or BORROWER .'
                                      ],
                                      [
                                        'That the GUARANTOR and every other person claiming by or through the GUARANTOR any interest in the CHATTEL will at all times at the sole cost of the GUARANTOR execute and do all such assurances and things for the future and better assigning of the CHATTEL unto COK SODALITY and enabling COK SODALITY to obtain possession of the same as may be by COK SODALITY lawfully required.'
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'REMOVAL OF PARTS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'Neither the BORROWER nor GUARANTOR shall:',
                            {
                              ol: [
                                [
                                  'Dismantle the CHATTEL or remove any part or parts thereof from the place or places where the said part is presently located unless done in the course of repair or improvement of the chattel by a person trained to conduct such repairs.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NO WAIVER OF RIGHTS OR REMEDIES',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'No delay, neglect, omission or forbearance on the part of COK SODALITY to take advantage of or enforce any right or remedy arising out of any breach or non-observance of any covenant or condition herein contained or implied shall be deemed to be or operate as a general waiver of such covenant or condition or the right to enforce or take advantage of the same in respect of any breach or non-observance thereof either original or recurring.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'INDEMNITY',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'COK SODALITY shall not be liable for any involuntary loss which may occur by reason of the exercise or execution (whether contemporaneously or otherwise) of any or all of its rights, remedies and powers conferred, given or implied by this indenture or by Law.'
                          ],
                          [
                            'The BORROWER hereby indemnifies COK SODALITY against any liability, loss or expense incurred by COK SODALITY as a consequence of:',
                            {
                              ol: [
                                [
                                  'any default in payment by the BORROWER of any sum under this Agreement when due; or'
                                ],
                                ['the occurrence of any Event of Default; or'],
                                [
                                  'any repayment or prepayment of the Facility or part thereof being received otherwise than as set out in this Agreement.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NO CHANGE IN STATUS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            ' COK SODALITY shall not be liable for any involuntary loss which may occur by reason of the exercise or execution (whether contemporaneously or otherwise) of any or all of its rights, remedies and powers conferred, given or implied by this indenture or by Law.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'CHANGE IN STATUS OR BUSINESS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'There will be no change in the nature of the business or status of the BORROWER or assets financed by the loan by reason of any sale, merger or consolidation without the written notice to COK SODALITY.'
                          ],
                          [
                            'That upon written notice to COK SODALITY of any change in the business or status of the BORROWER or assets financed COK SODALITY shall have the right, at its absolute discretion, to choose to discontinue its obligations and require immediate payment of any sums owed.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'SECURITIES',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'That this security shall not be affected by nor affect any other security which COK SODALITY may now or hereafter hold from the BORROWER or the GUARANTOR; and COK SODALITY shall be at liberty to realize the securities in such order and manner and to apply and appropriate:',
                            {
                              ol: [
                                [
                                  'any moneys at any time or times paid by or on behalf of the BORROWER; or resulting from a realization of this or any other security; or'
                                ],
                                [
                                  'any part thereof to such account or ITEM of indebtedness - in such sequence, priority and order as COK SODALITY may, in its absolute discretion, from time to time determine, any direction from the BORROWER or the GUARANTOR to the contrary notwithstanding.'
                                ]
                              ]
                            }
                          ],
                          [
                            'This security shall not be considered as satisfied or discharged by any intermediate payment of the whole or part of the moneys owing as aforesaid but shall constitute and be a continuing security to COK SODALITY notwithstanding:',
                            {
                              ol: [
                                [
                                  'any settlement of account; or other matter or thing whatsoever; and'
                                ],
                                [
                                  'shall be in addition to any other security or securities which COK SODALITY may from time to time hold or take from the BORROWER or the GUARANTOR and shall not be affected or prejudiced by or affect or prejudice in any way any other security or securities which COK SODALITY may now or hereafter hold or take from the BORROWER or the GUARANTOR'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'DEFAULT EVENT',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            "COK SODALITY shall be promptly notified of the occurrence of any event or circumstances which after notice or lapse of time or both, would constitute an Event of Default and of any matter which might materially or adversely affect the financial condition of the BORROWER or the BORROWER's assets."
                          ],
                          [
                            'COK SODALITY may (without prejudice to any of its other rights) upon and at any time after the happening of an Event of Default, by notice to the BORROWER declare that all sums owing by the BORROWER has become immediately due and payable, whereupon the BORROWER shall forthwith repay the same together with all interest due and all other sums payable under this Agreement.',
                            {
                              ol: [
                                [
                                  'There shall be an Event of Default if:',
                                  {
                                    ol: [
                                      [
                                        'the BORROWER fails to pay any sum payable by the BORROWER under this Agreement and/or any Securities when due, in the currency and manner provided in this Agreement; or'
                                      ],
                                      [
                                        'the BORROWER commits any breach of any provision of this Agreement and/or any Securities which COK SODALITY, in its, sole discretion, deems to be a materially adverse breach; or'
                                      ],
                                      [
                                        'the BORROWER commits any breach of any other provision of this Agreement and/or any Securities and such breach is, in the reasonable opinion of COK SODALITY, not capable of remedy or such breach is in the reasonable opinion of COK SODALITY capable of remedy and is not remedied within fourteen (14) days after the earlier of the date of notice by COK SODALITY requiring such remedy or the date on which the BORROWER first becomes aware of the breach; or'
                                      ],
                                      [
                                        'any misrepresentation: made or deemed to be made or repeated by the BORROWER in or pursuant to these presents and/or its securities; or'
                                      ],
                                      [
                                        "the BORROWER is adjudicated or found to be, insolvent/Bankrupt or stops or suspends payment of the BORROWER's debts or is (or is deemed to be) unable to or admits inability to pay debts as they fall due or proposes or enters into any voluntary arrangement or any composition or other arrangement for the benefit of the BORROWER's creditors generally or proceedings are commenced in relation to the BORROWER under any law, regulation or procedure relating to reconstruction or adjustment of debts; or"
                                      ],
                                      [
                                        "any other event or series of events or any circumstances whether related or not (including but without limitation any adverse change in the employment status, assets or financial condition of the BORROWER which, in the opinion of COK SODALITY, may have a material adverse effect on the BORROWER or the BORROWER 's ability or willingness to perform or comply with any of its obligations under this Agreement and/or its securities."
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'NOTICE',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'Any demand for payment or any other demand or notice hereunder:'
                          ],
                          [
                            'may be properly and effectually made by any Manager or Assistant Manager of any branch of COK SODALITY or by an agent, attorney or Attorney-at-Law of COK SODALITY;'
                          ],
                          [
                            'by letter sent by registered post, addressed to the BORROWER or the GUARANTOR at the address given in this security or at the last known place of business of the BORROWER or the GUARANTOR; and'
                          ],
                          [
                            'shall be deemed to have been made on the third day following the day the letter was posted in any post office in Jamaica.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'SATISFACTION OF THE LOAN',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'COK SODALITY may cause satisfaction to be entered on the margin of the record of these presents:',
                            {
                              ol: [
                                [
                                  'at the request of the BORROWER or the GUARANTOR; and'
                                ],
                                ['at COK SODALITY’s sole discretion; and'],
                                [
                                  'upon payment by the BORROWER or the GUARANTOR to COK SODALITY of:',
                                  {
                                    ol: [
                                      [
                                        'all moneys payable under this security and all costs and expenses which it may incur in lawfully seizing and removing the CHATTEL hereby assigned; and'
                                      ],
                                      [
                                        'any costs which it may have otherwise incurred in the protection, enforcement or realization of this security.'
                                      ]
                                    ]
                                  }
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'DUTY TO NOTIFY',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'As from the date hereof and so long as the loan or any part thereof or any interest thereon remains outstanding both the BORROWER and GUARANTOR have a duty to inform COK SODALITY promptly, in writing, of'
                          ],
                          [
                            'the happening of any Default Event or any event likely to have a substantial adverse effect on their: financial position or operations.'
                          ],
                          [
                            'any unusual or long-term contract likely by reason of its nature or magnitude which may affect the interest of COK SODALITY.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'AUTHORITY TO COMPLETE DOCUMENTS',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'COK SODALITY hereby irrevocably authorises COK SODALITY to supply any information on any document and/or complete any document signed by the BORROWER or the GUARANTOR in blank or incomplete, including the insertion of dates, amounts, names or descriptions so as to render such document compete and enforceable with respect to the Facility or any collateral provided by me to secure the same.'
                          ]
                        ]
                      }
                    ],
                    [
                      {
                        text: 'DATA PROTECTION',
                        decoration: 'underline'
                      },
                      {
                        ol: [
                          [
                            'As far as is reasonably required, for the service being offered by COK SODALITY in regards to the execution and performance of this Agreement and its securities: both the MORTGAGOR hereby consent to the collection, use, disclosure and processing of personal data and information provided by BORROWER and the MORTGAGOR to COK SODALITY or otherwise obtained.'
                          ],
                          [
                            'Personal data and information reasonably required, in accordance with CLAUSE 4.34.1 may be used by, disclosed to or shared with the following:',
                            {
                              ol: [
                                [
                                  'Senior Management and regular employees of the COK SODALITY’s various departments and branches for maintaining of accurate records and financial statements;'
                                ],
                                [
                                  'Local Authorities under statutory legislations such as the Credit Reporting Act;'
                                ],
                                [
                                  'debt collectors pursuant to COK SODALITY’s rights of seizure;'
                                ],
                                [
                                  'the Court and legal advisors pursuant to enforcement provisions;'
                                ],
                                [
                                  'Any other individual in the context of execution and performance of this Agreement and its securities.'
                                ]
                              ]
                            }
                          ]
                        ]
                      }
                    ]
                  ]
                }
              ],
              [
                {
                  text: 'CREDIT REPORTING',
                  decoration: 'underline'
                },
                {
                  ol: [
                    [
                      'The GUARANTOR hereby consents:',
                      {
                        ol: [
                          [
                            'To Credit Bureaus licensed under the Credit Reporting Act disclosing credit information about the GUARANTOR to COK SODALITY;'
                          ],
                          [
                            'To COK SODALITY disclosing credit information about the GUARANTOR to Credit Bureaus licensed under the Credit Reporting Act; and'
                          ],
                          [
                            'To COK SODALITY providing a signed copy of this agreement or any securities or otherwise to any Credit Bureaus licensed under the Credit Reporting Act by electronic or any other means.'
                          ]
                        ]
                      }
                    ]
                  ]
                }
              ]
            ]
          }
        ]
      ]),
      pageBreak: 'after',
      type: 'none'
    },
    {
      text: 'FIRST SCHEDULE',
      decoration: 'underline',
      bold: true,
      margin: [0, 0, 0, 30],
      alignment: 'center'
    },
    {
      table: {
        widths: ['*', '*', '*'],
        body: [
          [
            {
              text: 'ITEM 1',
              bold: false
            },
            {
              text: 'BORROWER',
              bold: false
            },
            {
              text: '',
              bold: false
            }
          ],
          [
            {
              text: '',
              bold: false
            },
            {
              text: 'Name',
              bold: false
            },
            {
              text: `${borrowerInfo.fullName}`,
              bold: false
            }
          ],
          [
            {},
            {
              text: 'Member Account No.',
              bold: false
            },
            {
              text: `${loan.account.mainAccountNumber}`,
              bold: false
            }
          ],
          [
            {},
            {
              text: 'Occupation:',
              bold: false
            },
            {
              text: `${
                borrowerInfo.employer.find((emp) => emp.isPrimary).profession
              }`,
              bold: false
            }
          ],
          [
            {},
            {
              text: 'TRN:',
              bold: false
            },
            {
              text: `${borrowerInfo.trn}`,
              bold: false
            }
          ],
          [
            {},
            {
              text: 'Address:',
              bold: false
            },
            {
              text: `${
                (borrowerInfo.livingAddress.street,
                borrowerInfo.livingAddress.city)
              }`,
              bold: false
            }
          ],
          [
            {},
            {
              text: 'Parish:',
              bold: false
            },
            {
              text: `${borrowerInfo.livingAddress.province}`,
              bold: false
            }
          ],
          [
            {
              text: 'ITEM 3',
              bold: false
            },
            {
              text: 'LOAN SUM:',
              bold: false
            },
            {
              text: `${loan.loanAmountInCurrency}`,
              bold: false
            }
          ],
          [
            {
              text: 'ITEM 4',
              bold: false
            },
            {
              text: 'COK INTEREST RATE:',
              bold: false
            },
            {
              text: `${loan.interest}%`,
              bold: false
            }
          ],
          [
            {
              text: 'ITEM 5',
              bold: false
            },
            {
              text: 'OFFER LETTER DATED',
              bold: false
            },
            {
              text: `${securityDocsUserData?.offer_letter_date}`,
              bold: false
            }
          ],
          [
            {
              text: 'ITEM 6',
              bold: false
            },
            {
              text: 'PROMISSORY NOTE AMOUNT:',
              bold: false
            },
            {
              text: `${loan.loanAmountInCurrency}`,
              bold: false
            }
          ],
          [
            {
              text: 'ITEM 7',
              bold: false
            },
            {
              text: 'STANDARD CUNA COVERAGE AMOUNT:',
              bold: false
            },
            {
              text: '',
              bold: false
            }
          ],
          [
            {
              text: 'ITEM 8',
              bold: false
            },
            {
              text: 'VALUATION COMPANY:',
              bold: false
            },
            {
              text: `${collaterals.motorVehicleCollaterals
                .map((col) => col.nameOfValuator)
                .join(',')}`,
              bold: false
            }
          ],
          [
            {
              text: 'ITEM 9',
              bold: false
            },
            {
              text: 'VALUATION DATE:',
              bold: false
            },
            {
              text: `${collaterals.motorVehicleCollaterals
                .map((col) => col.dateOfValuation)
                .join(',')}`,
              bold: false
            }
          ],
          [
            {
              text: 'ITEM 10',
              bold: false
            },
            {
              text: 'MARKET VALUE:',
              bold: false
            },
            {
              text: `${collaterals.motorVehicleCollaterals
                .map((col) => col.marketValue)
                .join(',')}`,
              bold: false
            }
          ],
          [
            {
              text: 'ITEM 11',
              bold: false
            },
            {
              text: 'OTHER SECURITY:',
              bold: false
            },
            {
              text: `${collaterals.cashCollaterals.reduce(
                (sum, collateral) => sum + collateral.pledgedAmount,
                0
              )}`,
              bold: false
            }
          ]
        ]
      },
      margin: [0, 0, 0, 30]
    },
    {
      text: 'SECOND SCHEDULE',
      alignment: 'center',
      decoration: 'underline',
      bold: true,
      margin: [0, 0, 0, 20]
    },
    ...collaterals.motorVehicleCollaterals.map((col, index) => {
      const obj = {
        margin: [0, 0, 0, 20],
        table: {
          widths: ['*', '*'],
          body: [
            [
              {
                text: '1. Make / Model:',
                bold: false
              },
              {
                text: `${col.makeModel}`,
                bold: false
              }
            ],
            [
              {
                text: '2. Year:',
                bold: false
              },
              {
                text: `${col.year}`,
                bold: false
              }
            ],
            [
              {
                text: '3. Colour:',
                bold: false
              },
              {
                text: `${col.color}`,
                bold: false
              }
            ],
            [
              {
                text: '4. Chassis No.:',
                bold: false
              },
              {
                text: `${col.chassisNumber}`,
                bold: false
              }
            ],
            [
              {
                text: '5. Engine No.:',
                bold: false
              },
              {
                text: `${col.engineNumber}`,
                bold: false
              }
            ]
          ]
        }
      };

      return index === collaterals.motorVehicleCollaterals.length
        ? (obj.pageBreak = 'after')
        : obj;
    }),
    {
      text: 'FOR AND ON BEHALF OF COK SODALITY CO-OPERATIVE CREDIT UNION LIMITED',
      margin: [0, 0, 0, 20],
      alignment: 'center',
      bold: true
    },

    {
      table: {
        widths: ['70%', '30%'],
        body: [
          [
            {
              text: [
                'SIGNED by',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'AUTHORIZED SIGNATORY (1)'
              ]
            },
            {}
          ],
          [
            {
              text: [
                'Title:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'Branch:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                }
              ]
            },
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                      decoration: 'underline',
                      alignment: 'center'
                    }
                  ],
                  [
                    {
                      text: 'SIGNATURE OF AUTHORIZED SIGNATORY (1)',
                      alignment: 'center'
                    }
                  ]
                ]
              },
              layout: {
                // default border settings
                hLineWidth: function (i, node) {
                  return 0;
                },
                vLineWidth: function (i, node) {
                  return 0;
                }
              }
            }
          ],
          [
            {
              text: [
                'SIGNED by',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'AUTHORIZED SIGNATORY (2)'
              ]
            },
            {}
          ],
          [
            {
              text: [
                'Title:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'Branch:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                }
              ]
            },
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                      decoration: 'underline',
                      alignment: 'center'
                    }
                  ],
                  [
                    {
                      text: 'SIGNATURE OF AUTHORIZED SIGNATORY (2)',
                      alignment: 'center'
                    }
                  ]
                ]
              },
              layout: {
                // default border settings
                hLineWidth: function (i, node) {
                  return 0;
                },
                vLineWidth: function (i, node) {
                  return 0;
                }
              }
            }
          ],
          [
            {
              text: [
                'before me:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'Witness'
              ]
            },
            {}
          ],
          [
            {
              text: [
                'at',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'in the parish of',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                }
              ]
            },
            {}
          ],
          [
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                      decoration: 'underline'
                    }
                  ],
                  [
                    {
                      text: 'SIGNATURE OF WITNESS:',
                      margin: [0, 0, 0, 10]
                    }
                  ]
                ]
              },
              layout: {
                // default border settings
                hLineWidth: function (i, node) {
                  return 0;
                },
                vLineWidth: function (i, node) {
                  return 0;
                }
              }
            },
            {}
          ]
        ]
      },
      layout: {
        // default border settings
        hLineWidth: function (i, node) {
          return 0;
        },
        vLineWidth: function (i, node) {
          return i === 1 ? 0.1 : 0;
        },
        paddingTop: function (i, node) {
          return 10;
        },
        paddingBottom: function (i, node) {
          return 10;
        }
      }
    },
    {
      margin: [0, 40, 0, 0],
      table: {
        widths: ['70%', '30%'],
        body: [
          [
            {
              text: 'SIGNED by MARCIA BROWN-ELLIOTT, MEMBER'
            },
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                      decoration: 'underline'
                    }
                  ],
                  ['Signature of Member Date :']
                ]
              },
              layout: {
                // default border settings
                hLineWidth: function (i, node) {
                  return 0;
                },
                vLineWidth: function (i, node) {
                  return 0;
                }
              }
            }
          ],
          [
            {
              text: [
                'before me:',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'Witness'
              ]
            },
            {}
          ],
          [
            {
              text: [
                'at',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                },
                'in the parish of',
                {
                  text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                  decoration: 'underline'
                }
              ]
            },
            {}
          ],
          [
            {
              widths: ['*'],
              table: {
                body: [
                  [
                    {
                      text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
                      decoration: 'underline'
                    }
                  ],
                  ['Signature of Witness	Date']
                ]
              },
              layout: {
                // default border settings
                hLineWidth: function (i, node) {
                  return 0;
                },
                vLineWidth: function (i, node) {
                  return 0;
                }
              }
            },
            {}
          ]
        ]
      },
      layout: {
        // default border settings
        hLineWidth: function (i, node) {
          return 0;
        },
        vLineWidth: function (i, node) {
          return i === 1 ? 0.1 : 0;
        },
        paddingTop: function (i, node) {
          return 10;
        },
        paddingBottom: function (i, node) {
          return 10;
        }
      }
    }
  ];

  let content = [...loanApplicationClause];

  switch (loan.loanType.toLowerCase().trim()) {
    case 'motor vehicle':
      content = content.concat(motorVehicleBlock);
      break;
    case 'cash secured':
      content = content.concat(cashSecuredBlock);
      break;
    default:
      content = content.concat(regularBlock);
  }

  return {
    info: {
      title: 'Loan Application',
      modDate: new Date(), // date the document was last modified
      creationDate: new Date() // date the document was created
    },

    defaultStyle: {
      font: 'Lora',
      color: '#0f172a',
      fontSize: 9,
      lineHeight: 1.3
    },

    content,

    footer: function (currentPage, pageCount) {
      return {
        text: `Page ${currentPage.toString()} of ${pageCount.toString()}`,
        alignment: 'center'
      };
    }
  };
};

function generateListItems(sentences) {
  return sentences.map((sentence, index) => {
    return {
      text: [
        {
          text: indexToAlphabeticMarker(index)
        },
        { text: sentence[0] }
      ]
    };
  });
}

function indexToAlphabeticMarker(index) {
  let marker = '';
  while (index >= 0) {
    marker = String.fromCharCode((index % 26) + 97) + marker;
    index = Math.floor(index / 26) - 1;
  }
  return `(${marker}) `;
}

function generateNumberedList(items, level = 0, prefix = '') {
  let result = [];

  items.forEach((item, index) => {
    if (typeof item === 'string') {
      let numberedItem = `${prefix} ${item}`;
      result.push(numberedItem);
    } else if (typeof item === 'object' && Array.isArray(item.ol)) {
      let subResult = item.ol.map((subItem, subIndex) => {
        if (typeof subItem === 'string') {
          return `${prefix} ${subItem}`;
        } else if (Array.isArray(subItem)) {
          return generateNumberedList(
            subItem,
            level + 1,
            `${prefix}.${subIndex + 1}`
          );
        }
      });
      result.push({ ol: subResult, type: 'none' });
    } else if (typeof item === 'object' && item.text) {
      result.push({ ...item, text: `${prefix} ${item.text}` });
    } else if (Array.isArray(item)) {
      result.push(generateNumberedList(item, level, index + 1));
    }
  });

  return result;
}
